import { COST_FAQ_ANSWER } from '../../../content/pricingFigures';
/**
 * roleConfigs — the per-role content registry that drives the shared
 * <RoleLandingPage> template at /ai-employees/ai-{slug}.
 *
 * ⚠️ READ ./README.md FIRST — it is the full playbook (recipe, content rules,
 * prerender gotchas, verification checklist, scaling gates). The summary
 * below is the short version.
 *
 * THIS IS THE "TEMPLATING ENGINE" for the programmatic-SEO role pages.
 * One entry here = one live page. To add role #11..#100:
 *   1. Append a config object below (follow the shape exactly).
 *   2. Add "/ai-employees/ai-{slug}" to reactSnap.include in package.json.
 *   3. Add the URL to STATIC_PAGES in py/cellcog/api/seo.py.
 *   4. Add a 1200x630 OG JPG at public/assets/ai-employees/{slug}-og.jpg.
 * Nothing else. The route in App.js is dynamic (:roleSlug) and validates
 * against this registry.
 *
 * CONTENT RULES for this surface (inherited from the pSEO playbook, keep):
 *  - No em dashes in copy (use colons/commas).
 *  - Banned words: revolutionize, game-changer, cutting-edge, unleash,
 *    supercharge, seamless, robust.
 *  - Visible copy says "CellCog AI Employee" (brand casing: CellCog).
 *  - Article agreement: "an" before vowel-sound roles (an SEO specialist).
 *  - No human salary or human-hire comparison on role pages (Nitish, Oct 14 2026).
 *  - CellCog cost (canon Sep 6 2026): no cost figure on role pages; the cost
 *    FAQ renders COST_FAQ_ANSWER from src/content/pricingFigures.json.
 *  - No recipient email anywhere in page code (lead destination lives in the
 *    Web3Forms dashboard).
 *
 * Source content: extracted + audited from the pSEO handoff bundle
 * (agent_docs/ai_employee_role_seo_pages_prd.md).
 */

/** Shared teams FAQ, appended to every role page's FAQ list by
 *  RoleLandingPage (positioning canon, Jul 2026: employees form teams and
 *  manage each other — CellCog's own org is the proof). */
export const SHARED_TEAMS_FAQ = {
  q: 'Can my AI employee work with other AI employees?',
  a: 'Yes. CellCog AI Employees form teams: they delegate tasks to each other\u2019s boards, communicate over their own email, and a manager employee can onboard and unblock their team without waiting on you. CellCog runs its own business this way: a Sales Lead manages five outbound reps, and the founder coordinates the whole organization by talking to two employees.',
  link: { href: '/ai-organization', label: 'See the live org chart of CellCog\u2019s AI organization' },
};

/** The 7 traits every CellCog AI Employee shares (identical across roles —
 *  rendered compact to keep the unique-content ratio healthy). */
export const SHARED_TRAITS = [
  { title: 'Works toward measurable goals', body: 'and the KPIs you set' },
  { title: 'Maintains long-term memory', body: 'across conversations and tasks' },
  { title: 'Has their own identity', body: 'a name, a role, a profile' },
  { title: 'Owns a dedicated inbox', body: 'email them like a coworker' },
  { title: 'Manages their own task list', body: 'and prioritizes the work' },
  { title: 'Requests human approval', body: 'whenever you require it' },
  { title: 'Starts work on schedules', body: 'events, or trigger conditions' },
];

export const ROLE_CONFIGS = {
  "ai-executive-assistant": {
    slug: "ai-executive-assistant",
    relatedPosts: ["best-ai-executive-assistants", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Executive Assistant",
    roleNoun: "executive assistant",
    article: "an",
    seo: {
      title: "AI Executive Assistant: Hire a CellCog AI Employee | CellCog",
      description: "AI Executive Assistant from CellCog AI Employee: triage your inbox, manage your calendar, and prep briefings on demand. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee triages the executive's inbox, drafts replies, flags what needs attention, coordinates complex calendars, and schedules meetings across time zones.",
    capabilities: [
      { title: "Manage the inbox", body: "Manage and triage the executive's inbox, drafting replies and flagging what needs attention." },
      { title: "Coordinate calendars", body: "Coordinate complex calendars, schedule meetings across time zones, and resolve conflicts." },
      { title: "Prepare briefing documents", body: "Prepare briefing documents and pre-read materials ahead of meetings." },
      { title: "Attend your meetings", body: "Join Google Meet, Zoom or Microsoft Teams under their own name, answer what they are authorized to, defer the rest, and file the notes and action items after the call." },
      { title: "Book travel", body: "Book travel and build detailed itineraries." },
    ],
    prompts: [
      {
        q: "Take these 40 minutes of meeting notes and turn them into a one-page summary with decisions, action items by owner, and a follow-up email for the team.",
        deliverable: "a PDF summary plus a ready-to-send email draft",
      },
      {
        q: "Here are 12 calendar invites for next week. Find the conflicts, suggest a resolution for each, and draft a rescheduling note I can send.",
        deliverable: "a conflict report with proposed fixes and draft messages",
      },
      {
        q: "Build a travel itinerary for my Tuesday to Thursday trip to Chicago: flights, hotel near the venue, and a day-by-day schedule with buffer time.",
        deliverable: "a formatted itinerary PDF",
      },
      {
        q: "Turn this stack of 18 receipts into an expense report grouped by category with totals and a summary line for accounting.",
        deliverable: "an expense spreadsheet with categorized totals",
      },
      {
        q: "Draft a briefing doc for my 2pm with the Acme team: who they are, our history with them, open items, and three questions I should ask.",
        deliverable: "a one-page briefing PDF",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own executive assistant work",
        body: "Founders hand off the repetitive executive assistant work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing executive assistant duties",
        body: "Small teams point CellCog AI Employee at the executive assistant work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running executive assistant work at scale",
        body: "Agencies produce executive assistant deliverables across many clients at once, growing capacity without adding headcount.",
      },
    ],
    limits: [
      {
        title: "Reading the room",
        body: "A human EA senses when you are stressed and quietly reprioritizes your day.",
      },
      {
        title: "Sensitive conversations",
        body: "Smoothing a conflict with an important client by phone is a human relationship task.",
      },
      {
        title: "Owning the final call",
        body: "When priorities truly collide, a trusted human makes the judgment call.",
      },
    ],
    faq: [
      {
        q: "Can CellCog AI Employee really manage my calendar like a human EA?",
        a: "They spot conflicts, propose resolutions, and draft the messages to fix them. You approve the changes, so they do the legwork while you keep control.",
      },
      {
        q: "Can they join my meetings?",
        a: "Yes. Add their email to the calendar invite and they join Google Meet, Zoom or Microsoft Teams under their own name, camera off. They listen to the room, answer when addressed within what you have authorized, and never commit you to a date, price or promise in the call. The notes, decisions and action items land on their task board afterward.",
      },
      {
        q: "What happens when they do not have context about a person or meeting?",
        a: "They work from what you connect, so the more access they have to past notes and emails, the more their briefings reflect your real history.",
      },
      {
        q: "Can they actually send emails on my behalf?",
        a: "Yes, once you connect your inbox and approve. Sensitive sends route to you first, so nothing goes out without your sign-off.",
      },
      {
        q: "Is my calendar and inbox data private?",
        a: "Your data stays yours and is used only to do your work. You control what you connect and can disconnect anytime.",
      },
      {
        q: "How is this different from a scheduling tool like Calendly?",
        a: "A scheduling tool just books slots. CellCog AI Employee handles the wider EA job: briefings, expenses, travel, correspondence, and research.",
      },
      {
        q: "How does the cost compare to hiring an executive assistant?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Executive Assistant.",
      },
    ],
    ctaBand: {
      title: "Put your AI Executive Assistant to work today",
      body: "Hand them your last meeting's notes and get back a clean summary with action items in minutes.",
    },
  },
  "ai-marketing-manager": {
    slug: "ai-marketing-manager",
    roleDisplay: "AI Marketing Manager",
    roleNoun: "marketing manager",
    article: "a",
    seo: {
      title: "AI Marketing Manager: Hire a CellCog AI Employee | CellCog",
      description: "AI Marketing Manager from CellCog AI Employee: plan campaigns, write copy, and produce assets on demand. See real prompts and a side-by-side comparison.",
    },
    hook: "A CellCog AI Employee plans and runs multi-channel campaigns, writes briefs and positioning, and produces the creative.",
    capabilities: [
      { title: "Plan campaigns", body: "Plan and run multi-channel marketing campaigns from concept to launch." },
      { title: "Write briefs and positioning", body: "Write campaign briefs, positioning, and messaging." },
      { title: "Produce marketing assets", body: "Produce marketing assets: social posts, landing pages, one-pagers, and ad creative." },
      { title: "Analyze performance", body: "Analyze campaign performance and report on what is working." },
      { title: "Research competitors", body: "Research competitors and market positioning." },
    ],
    prompts: [
      {
        q: "Build a 6-week launch campaign for our new feature: a week-by-week content calendar, 10 social posts, an email announcement, and a one-page launch brief.",
        deliverable: "a campaign brief PDF, a content calendar, and ready-to-use copy",
      },
      {
        q: "Write three positioning angles for our product aimed at small agencies, and pick the strongest with your reasoning.",
        deliverable: "a positioning doc with a recommended angle",
      },
      {
        q: "Create a 10-slide pitch deck for our Q3 marketing plan with goals, channels, budget split, and projected results.",
        deliverable: "a slide deck PDF",
      },
      {
        q: "Analyze last quarter's campaign numbers I am pasting below and tell me what worked, what did not, and where to spend next.",
        deliverable: "a performance report with recommendations",
      },
      {
        q: "Generate five ad images for our summer promotion using this product photo, in a bright lifestyle style.",
        deliverable: "five ready-to-use ad images",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own marketing manager work",
        body: "Founders hand off the repetitive marketing manager work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing marketing manager duties",
        body: "Small teams point CellCog AI Employee at the marketing manager work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running marketing manager work at scale",
        body: "Agencies produce marketing manager deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Your brand instinct",
        body: "They learn your voice from the examples you give them. The call on what sounds like you stays yours.",
      },
      {
        title: "Leading the team",
        body: "Rallying sales, product, and design around a campaign stays with you. Your AI Marketing Manager hands them the brief and the assets.",
      },
      {
        title: "The call in a crisis",
        body: "When a campaign backfires publicly, you make the fast, accountable call. They pull the numbers and draft the response.",
      },
    ],
    comparison3: {
      title: "Three ways to run your marketing",
      cols: ["Doing it yourself", "A marketing tool alone", "AI Marketing Manager"],
      rows: [
        { label: "Plans the campaign", cells: ["You, when there is time", "Templates only", "A full plan with a calendar"] },
        { label: "Produces copy, images, and video", cells: ["You, one piece at a time", "Copy suggestions", "The whole asset set"] },
        { label: "Covers channels at once", cells: ["One at a time", "Schedules what you made", "Plans every channel in parallel"] },
        { label: "Analyzes performance", cells: ["Manual, now and then", "Dashboards you read", "Paste data, analysis in minutes"] },
        { label: "Asks before publishing", cells: ["n/a", "n/a", "Yes, by default"] },
      ],
    },
    faq: [
      {
        q: "Can CellCog AI Employee really run a campaign end to end?",
        a: "They plan the campaign, write the assets, build the calendar, and produce the creative. You approve the direction and own the strategy, while the production work that eats most of the week gets done fast.",
      },
      {
        q: "Will the copy actually sound like our brand?",
        a: "When you give them examples of your existing content and voice, they match your tone. The more brand context you connect, the closer the output gets to something you would publish as-is.",
      },
      {
        q: "Can they make the images and videos too, or just the words?",
        a: "Both. They generate images, short videos, slide decks, and PDFs alongside the copy, so a single campaign request can return the whole asset set.",
      },
      {
        q: "What happens when a campaign underperforms?",
        a: "Paste in the numbers and they analyze what worked and what did not, then recommend where to shift spend. You make the call; they give you the analysis to make it faster.",
      },
      {
        q: "What stays with me?",
        a: "Strategy, direction, and final approval. They do the production work: the plan, the copy, the creative, and the analysis, so you can run full multi-channel campaigns while you keep the strategy.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Marketing Manager.",
      },
    ],
    ctaBand: {
      title: "Put your AI Marketing Manager to work today",
      body: "Ask them to plan your next product launch and get a full campaign brief and content calendar back.",
    },
  },
  "ai-project-manager": {
    slug: "ai-project-manager",
    relatedPosts: ["best-ai-project-management-tools", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Project Manager",
    roleNoun: "project manager",
    article: "a",
    seo: {
      title: "AI Project Manager: Hire a CellCog AI Employee | CellCog",
      description: "AI Project Manager from CellCog AI Employee: build plans, status reports, and risk registers on demand. See real prompts and an honest comparison.",
    },
    hook: "A CellCog AI Employee builds project plans with milestones, owners, and timelines, and turns meeting notes into action items and status updates.",
    capabilities: [
      { title: "Build project plans", body: "Build project plans with milestones, owners, and timelines." },
      { title: "Sit in the standup and file the actions", body: "Attend project meetings on Google Meet, Zoom or Microsoft Teams, answer the status questions they are authorized to answer, and turn what was decided into action items and status updates." },
      { title: "Create status reports", body: "Create status reports and stakeholder updates." },
      { title: "Draft project briefs", body: "Draft project briefs and scope documents." },
      { title: "Build risk registers", body: "Build risk registers and mitigation plans." },
    ],
    prompts: [
      {
        q: "Turn this messy task list and these meeting notes into a project plan with phases, milestones, owners, dependencies, and a one-page status report.",
        deliverable: "a project plan and a status report",
      },
      {
        q: "Build a risk register for our product launch: top 10 risks, likelihood, impact, and a mitigation for each.",
        deliverable: "a risk register",
      },
      {
        q: "Write a stakeholder update for this week: what shipped, what is blocked, and what is next, in a clear executive tone.",
        deliverable: "a ready-to-send status update",
      },
      {
        q: "Create a project brief for our website redesign with scope, goals, timeline, and success metrics.",
        deliverable: "a project brief PDF",
      },
      {
        q: "Turn our roadmap into a visual timeline I can present, with phases and key dates.",
        deliverable: "a timeline slide for presenting",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own project manager work",
        body: "Founders hand off the repetitive project manager work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing project manager duties",
        body: "Small teams point CellCog AI Employee at the project manager work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running project manager work at scale",
        body: "Agencies produce project manager deliverables across many clients at once, growing capacity without adding headcount.",
      },
    ],
    limits: [
      {
        title: "Leading people through change",
        body: "Motivating a team, resolving interpersonal friction, and earning buy-in are human leadership skills an AI employee cannot perform.",
      },
      {
        title: "Reading the room in a tough meeting",
        body: "A human PM senses when a stakeholder is unhappy and steers the conversation in the moment.",
      },
      {
        title: "Owning the outcome",
        body: "Someone has to be accountable for delivery and make the hard trade-off calls; your AI Project Manager produces the plan, a person stands behind it.",
      },
    ],
    faq: [
      {
        q: "Can CellCog AI Employee really manage a project?",
        a: "They build the plans, status reports, risk registers, and dashboards that make up the documentation side of the job. A human still leads the team and owns delivery, but the planning and reporting load gets handled fast.",
      },
      {
        q: "Can they keep my project plan updated as things change?",
        a: "Give them the latest task data or notes and they regenerate the plan and status report. Keeping documents current, which usually eats hours, becomes a quick prompt.",
      },
      {
        q: "What happens when a project goes off the rails?",
        a: "They can surface what is blocked or behind and draft the status update, but steering the team back on track is human work. Use your AI Project Manager to see the situation clearly, then lead the response yourself.",
      },
      {
        q: "Can they produce reports my stakeholders will actually read?",
        a: "Yes, they write clear executive-ready status updates and build visual timelines and dashboards. You review the framing, and they handle the assembly.",
      },
      {
        q: "Can they attend our project meetings?",
        a: "Yes. Invite them to the Google Meet, Zoom or Microsoft Teams the way you would a colleague. They join under their own name, camera off, answer status questions within what you have authorized, defer anything that would commit the project to a date or scope, and file the decisions and action items on their task board after the call.",
      },
      {
        q: "Do they replace tools like Asana or Jira?",
        a: "No, they complement them. They turn raw task data and notes into plans, reports, and dashboards, while your task tracker stays the system of record.",
      },
      {
        q: "How does the cost compare to hiring a project manager?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Project Manager.",
      },
    ],
    ctaBand: {
      title: "Put your AI Project Manager to work today",
      body: "Give them your task list and meeting notes and get back a clean project plan with owners and a status report.",
    },
  },
  "ai-research-assistant": {
    slug: "ai-research-assistant",
    roleDisplay: "AI Research Assistant",
    roleNoun: "research assistant",
    article: "a",
    seo: {
      title: "AI Research Assistant: Hire a CellCog AI Employee | CellCog",
      description: "AI Research Assistant from CellCog AI Employee: deep research, summaries, and cited reports on demand. See real prompts and a side-by-side comparison.",
    },
    hook: "A CellCog AI Employee runs deep research on any topic, synthesizes sources into cited reports, and summarizes long documents and papers.",
    capabilities: [
      { title: "Run deep research", body: "Run deep research on a topic and synthesize sources into a report." },
      { title: "Summarize long documents", body: "Summarize long documents, papers, and reports." },
      { title: "Build literature reviews", body: "Build literature reviews with proper citations." },
      { title: "Compare options and vendors", body: "Compare options or vendors in a structured analysis." },
      { title: "Gather data", body: "Gather and organize data from multiple sources." },
    ],
    prompts: [
      {
        q: "Research the electric vehicle charging market. Cover market size, key players, growth trends, and main challenges, with citations I can verify.",
        deliverable: "a cited research report",
      },
      {
        q: "Summarize these three academic papers I am uploading and tell me where they agree and disagree.",
        deliverable: "a comparative summary",
      },
      {
        q: "Build a literature review on remote work productivity from the last five years, with proper citations.",
        deliverable: "a cited literature review",
      },
      {
        q: "Compare four project management tools on price, features, and reviews, and recommend the best for a 10-person team.",
        deliverable: "a comparison report with a recommendation",
      },
      {
        q: "Turn this research report into a 12-slide deck I can present to my team.",
        deliverable: "a slide deck PDF",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own research assistant work",
        body: "Founders hand off the repetitive research assistant work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing research assistant duties",
        body: "Small teams point CellCog AI Employee at the research assistant work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running research assistant work at scale",
        body: "Agencies produce research assistant deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Original research",
        body: "Designing and running experiments or field studies stays with you. They summarize and analyze what you gather.",
      },
      {
        title: "Expert judgment",
        body: "What a finding really means in your context is your call. They bring the sources and the synthesis.",
      },
      {
        title: "Accountability for accuracy",
        body: "Your name goes on the work. They cite their sources so you can check the hardest claims yourself.",
      },
    ],
    comparison3: {
      title: "Three ways to research a topic",
      cols: ["Doing it yourself", "A chatbot", "AI Research Assistant"],
      rows: [
        { label: "Research turnaround", cells: ["Days per topic", "A quick answer", "A cited report in minutes"] },
        { label: "Sources reviewed", cells: ["As many as time allows", "A quick search per answer", "Many, gathered into one cited report"] },
        { label: "Output format", cells: ["Usually a document", "A chat reply", "Report, deck, or bibliography"] },
        { label: "Takes work by email", cells: ["n/a", "No", "Yes, at their own address"] },
      ],
    },
    faq: [
      {
        q: "Can CellCog AI Employee do real research or do they just make things up?",
        a: "They search real sources and give you citations you can verify, which is the point of the design. You should still check the key sources, but they do the gathering and synthesis fast.",
      },
      {
        q: "Are the citations real and checkable?",
        a: "Yes, your AI Research Assistant provides source links so you can verify claims yourself. Verifying the most important ones is good practice, and your AI Research Assistant makes that easy by showing where each fact came from.",
      },
      {
        q: "What happens when sources disagree?",
        a: "Your AI Research Assistant surfaces the disagreement instead of hiding it, showing you where sources conflict so you can judge. That transparency is more useful than a single confident answer.",
      },
      {
        q: "Can they research niche or technical topics?",
        a: "They handle a wide range of topics and pull from authoritative sources. For highly specialized fields, treat their report as a strong first pass that a domain expert refines.",
      },
      {
        q: "How is this different from just using a search engine?",
        a: "A search engine gives you links to read. CellCog AI Employee reads across many sources and returns a synthesized, cited report or deck you can use directly.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Research Assistant.",
      },
    ],
    ctaBand: {
      title: "Put your AI Research Assistant to work today",
      body: "Give them a topic and get back a cited research report you can verify and use right away.",
    },
  },
  "ai-content-writer": {
    slug: "ai-content-writer",
    roleDisplay: "AI Content Writer",
    roleNoun: "content writer",
    article: "a",
    seo: {
      title: "AI Content Writer: Hire a CellCog AI Employee | CellCog",
      description: "AI Content Writer from CellCog AI Employee: blog posts, emails, and landing page copy on demand. See real prompts and how they compare.",
    },
    hook: "A CellCog AI Employee writes blog posts and articles from a brief, and drafts social copy and captions.",
    capabilities: [
      { title: "Write blog posts", body: "Write blog posts and articles from a brief or topic." },
      { title: "Draft social copy", body: "Draft social media copy and captions." },
      { title: "Write email newsletters", body: "Write email newsletters and sequences." },
      { title: "Produce landing page copy", body: "Produce landing page and website copy." },
      { title: "Repurpose long content", body: "Repurpose long content into threads, posts, and snippets." },
    ],
    prompts: [
      {
        q: "Write a 1,200-word blog post on why small businesses should care about email marketing, practical with examples and a call to action.",
        deliverable: "a finished, ready-to-publish blog post",
      },
      {
        q: "Turn this blog post into a Twitter thread, a LinkedIn post, and three short captions.",
        deliverable: "repurposed social copy in three formats",
      },
      {
        q: "Write a 4-email welcome sequence for new newsletter subscribers in a friendly, helpful tone.",
        deliverable: "four ready-to-send email drafts",
      },
      {
        q: "Write homepage copy for a bookkeeping service aimed at freelancers: headline, subhead, three benefits, and a CTA.",
        deliverable: "ready-to-use landing page copy",
      },
      {
        q: "Edit and tighten this 800-word draft I am pasting, keep my voice, and cut the fluff.",
        deliverable: "a clean, edited version with changes noted",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own content writer work",
        body: "Founders hand off the repetitive content writer work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing content writer duties",
        body: "Small teams point CellCog AI Employee at the content writer work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running content writer work at scale",
        body: "Agencies produce content writer deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Your lived experience",
        body: "The stories only you can tell stay yours. Give them your notes and they build the draft around them.",
      },
      {
        title: "Deep subject expertise",
        body: "Your industry insight is the edge. They research and draft; you add what only an insider knows.",
      },
      {
        title: "Your brand story",
        body: "The long arc of your brand narrative is yours to shape. They keep every piece consistent with it.",
      },
    ],
    comparison3: {
      title: "Three ways to get content written",
      cols: ["Doing it yourself", "An AI writing app", "AI Content Writer"],
      rows: [
        { label: "Drafting speed", cells: ["Hours per piece", "Fast, then heavy editing", "A full draft in minutes"] },
        { label: "Learns your voice", cells: ["Yes", "Brand voice settings", "From your examples, kept on file"] },
        { label: "Repurposing", cells: ["Manual rewrites", "One prompt at a time", "One piece into many formats"] },
        { label: "Works from a calendar", cells: ["When there is time", "No", "Yes, on schedule"] },
      ],
    },
    faq: [
      {
        q: "Will content from CellCog AI Employee sound generic?",
        a: "When you give them your voice and examples, they match your style. The best results come from treating their draft as a strong first version that you add your own perspective to.",
      },
      {
        q: "Can they really write in my brand voice?",
        a: "Yes, give them samples of your existing writing and they adapt to your tone. The more voice context you provide, the closer the draft gets to publish-ready.",
      },
      {
        q: "What happens when the draft is not quite right?",
        a: "You tell them what to change and they revise in seconds. Iterating is fast, so you can try several versions in one sitting.",
      },
      {
        q: "Will search engines penalize AI-written content?",
        a: "Search engines reward helpful, original content regardless of how it was drafted. Review and add your own expertise to your AI Content Writer's drafts, which is good practice for any content.",
      },
      {
        q: "Can they write long-form and short-form?",
        a: "Both. They handle blog posts, landing pages, email sequences, social captions, and product descriptions, and they can repurpose one piece into many formats.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Content Writer.",
      },
    ],
    ctaBand: {
      title: "Put your AI Content Writer to work today",
      body: "Ask them to write your next blog post and get back a finished, structured draft in your voice.",
    },
  },
  "ai-seo-specialist": {
    slug: "ai-seo-specialist",
    roleDisplay: "AI SEO Specialist",
    roleNoun: "SEO specialist",
    article: "an",
    seo: {
      title: "AI SEO Specialist: Hire a CellCog AI Employee | CellCog",
      description: "AI SEO Specialist from CellCog AI Employee: keyword research, audits, and optimized content on demand. See real prompts and a side-by-side comparison.",
    },
    hook: "A CellCog AI Employee runs keyword research, finds ranking opportunities, and audits pages for on-page SEO issues and fixes.",
    capabilities: [
      { title: "Run keyword research", body: "Run keyword research and find ranking opportunities." },
      { title: "Audit on-page SEO", body: "Audit pages for on-page SEO issues and fixes." },
      { title: "Write SEO-optimized articles", body: "Write SEO-optimized articles and landing page copy." },
      { title: "Analyze competitor content", body: "Analyze competitor content and find gaps." },
      { title: "Build content briefs", body: "Build content briefs for writers with target keywords and structure." },
    ],
    prompts: [
      {
        q: "Do keyword research for a project management software blog. Find 20 low-competition keywords, group them into clusters, and write three SEO article briefs with titles, headings, and target keywords.",
        deliverable: "a keyword spreadsheet plus ready-to-write briefs",
      },
      {
        q: "Audit this landing page URL for on-page SEO issues and give me a prioritized fix list.",
        deliverable: "an on-page audit report",
      },
      {
        q: "Write a 1,500-word SEO-optimized article targeting the keyword 'remote team management tips' with proper headings and a meta description.",
        deliverable: "a finished, optimized article",
      },
      {
        q: "Compare our top competitor's blog to ours and find 10 content gaps we should fill to rank.",
        deliverable: "a content gap analysis",
      },
      {
        q: "Generate meta titles, descriptions, and FAQ schema markup for these five product pages.",
        deliverable: "ready-to-paste meta tags and JSON-LD schema",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own SEO specialist work",
        body: "Founders hand off the repetitive SEO specialist work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing SEO specialist duties",
        body: "Small teams point CellCog AI Employee at the SEO specialist work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running SEO specialist work at scale",
        body: "Agencies produce SEO specialist deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Link-building relationships",
        body: "Earning backlinks through real relationships stays with you. They find the targets and draft the outreach.",
      },
      {
        title: "Strategy after big algorithm shifts",
        body: "When a major update changes the game, the strategic call is yours. They re-audit your pages and show what moved.",
      },
      {
        title: "Technical site overhauls",
        body: "Engineering trade-offs on a complex site stay with your developers. Your AI SEO Specialist writes the audit and the fix list.",
      },
    ],
    comparison3: {
      title: "Three ways to run SEO",
      cols: ["Doing it yourself", "An SEO tool", "AI SEO Specialist"],
      rows: [
        { label: "Keyword research", cells: ["Days per project", "Data you interpret", "A prioritized keyword set in minutes"] },
        { label: "Content briefs", cells: ["A few per week", "Briefs you review and finish", "Briefs written into full articles"] },
        { label: "On-page audits", cells: ["Manual, page by page", "A list of issues", "Audits plus the fixes and schema"] },
        { label: "Writes the articles", cells: ["You", "No", "Yes, from the briefs"] },
      ],
    },
    faq: [
      {
        q: "Can CellCog AI Employee really find keywords I can rank for?",
        a: "They research keywords, estimate competition, and group them into clusters you can build content around. You bring the judgment on business fit; they do the research legwork fast.",
      },
      {
        q: "Will the articles they write actually rank?",
        a: "They write optimized, well-structured content with the right keywords and headings, which is the foundation of ranking. Rankings still depend on your site authority and competition, so your AI SEO Specialist gives you a strong starting point, not a guarantee.",
      },
      {
        q: "Can they handle technical SEO, not just content?",
        a: "They audit on-page issues, generate schema markup, and build internal linking plans. Deep server-level or engineering-heavy fixes still need your developers, but most on-page work your AI SEO Specialist handles.",
      },
      {
        q: "What happens after a Google algorithm update?",
        a: "They can re-audit your pages and content against current best practices, but reading the strategic implications of a major update is your call. Use them to do the audit, then decide direction yourself.",
      },
      {
        q: "Is the content unique or will it be flagged as AI?",
        a: "They produce original content tailored to your topic and brief. You should still review and add your own expertise, which is good SEO practice regardless of who wrote the draft.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI SEO Specialist.",
      },
    ],
    ctaBand: {
      title: "Put your AI SEO Specialist to work today",
      body: "Ask them to find 20 keywords you can rank for and turn them into ready-to-write content briefs.",
    },
  },
  "ai-social-media-manager": {
    slug: "ai-social-media-manager",
    relatedPosts: ["best-ai-social-media-managers", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Social Media Manager",
    roleNoun: "social media manager",
    article: "a",
    seo: {
      title: "AI Social Media Manager: Hire a CellCog AI Employee | CellCog",
      description: "AI Social Media Manager from CellCog AI Employee: plan calendars, write posts, and create visuals on demand. See prompts and a side-by-side comparison.",
    },
    hook: "A CellCog AI Employee plans and builds content calendars across platforms, and writes platform-specific posts and captions.",
    capabilities: [
      { title: "Plan content calendars", body: "Plan and build a content calendar across platforms." },
      { title: "Write platform-specific posts", body: "Write platform-specific posts and captions." },
      { title: "Create images", body: "Create images, graphics, and short videos for posts." },
      { title: "Draft engagement replies", body: "Draft engagement replies and community responses." },
      { title: "Research trending topics", body: "Research trending topics and formats." },
    ],
    prompts: [
      {
        q: "Plan a month of social content for my coffee brand: a 4-week calendar with 20 posts, the caption for each, and five product images I can use.",
        deliverable: "a content calendar, captions, and generated images",
      },
      {
        q: "Write 10 platform-specific posts about our new feature, tuned for LinkedIn, Instagram, and X.",
        deliverable: "10 platform-tuned posts",
      },
      {
        q: "Generate three short vertical videos for a product teaser using this image and a punchy script.",
        deliverable: "three short videos ready to post",
      },
      {
        q: "Research what is trending in our niche this week and suggest five post ideas that fit our brand.",
        deliverable: "a trend report with post ideas",
      },
      {
        q: "Turn this blog post into a week of social content across formats.",
        deliverable: "a week of repurposed posts",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own social media manager work",
        body: "Founders hand off the repetitive social media manager work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing social media manager duties",
        body: "Small teams point CellCog AI Employee at the social media manager work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running social media manager work at scale",
        body: "Agencies produce social media manager deliverables across many clients at once, growing capacity without adding headcount.",
      },
    ],
    limits: [
      {
        title: "Real-time community interaction",
        body: "Replying live to followers and reading the mood of a moment is a human touch that builds genuine connection.",
      },
      {
        title: "Riding cultural moments",
        body: "Knowing when and how to join a fast-moving cultural trend tastefully takes human judgment about brand fit.",
      },
      {
        title: "Influencer and partner relationships",
        body: "Building real relationships with creators and partners is people work your AI Social Media Manager supports but does not do.",
      },
    ],
    faq: [
      {
        q: "Can CellCog AI Employee really run my social media?",
        a: "They plan the calendar, write the posts, and create the images and videos, which is most of the workload. A human still owns live engagement and brand judgment, but the production gets done fast.",
      },
      {
        q: "Can they make images and videos, not just captions?",
        a: "Yes, they generate on-brand images and short videos alongside the copy, so a single request can return a full week of ready-to-post content.",
      },
      {
        q: "Will the posts actually fit each platform?",
        a: "Your AI Social Media Manager tunes tone and format for each platform when you tell them where the post is going. You review before posting, so platform fit stays under your eye.",
      },
      {
        q: "What happens with comments and DMs?",
        a: "Your AI Social Media Manager can draft replies, but live community interaction is best kept human. Use them to produce and schedule content, and keep the real-time conversation personal.",
      },
      {
        q: "Can they keep my feed consistent and on brand?",
        a: "Give them your brand colors, voice, and examples and they produce consistent content. Consistency is exactly where they help most, since they never run out of time to post.",
      },
      {
        q: "How does the cost compare to hiring a social media manager?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Social Media Manager.",
      },
    ],
    ctaBand: {
      title: "Put your AI Social Media Manager to work today",
      body: "Ask them to plan a month of posts for your brand and get back a calendar, captions, and images.",
    },
  },
  "ai-community-manager": {
    slug: "ai-community-manager",
    roleDisplay: "AI Community Manager",
    roleNoun: "community manager",
    article: "a",
    seo: {
      title: "AI Community Manager: Hire a CellCog AI Employee | CellCog",
      description: "AI Community Manager from CellCog AI Employee: read sentiment, plan content, and draft announcements on demand. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee plans and schedules community content across Discord, Slack, and forums, and drafts announcements, invites, and updates.",
    capabilities: [
      { title: "Plan community content", body: "Plan and schedule community content across Discord, Slack, and forums." },
      { title: "Draft announcements", body: "Draft announcements, event invites, and community updates." },
      { title: "Summarize sentiment", body: "Summarize community sentiment and surface recurring themes." },
      { title: "Write welcome sequences", body: "Write welcome sequences and onboarding messages for new members." },
      { title: "Research discussion topics", body: "Research what topics and formats drive engagement." },
    ],
    prompts: [
      {
        q: "Read these 200 community messages from the past week and give me a sentiment summary with the top five themes, what people love, and what frustrates them.",
        deliverable: "a sentiment report with themes and quotes",
      },
      {
        q: "Plan a month of community programming: weekly events, discussion prompts, and an announcement calendar.",
        deliverable: "a programming calendar",
      },
      {
        q: "Write a warm welcome sequence of three messages for new members joining our Discord.",
        deliverable: "three ready-to-post welcome messages",
      },
      {
        q: "Turn yesterday's AMA transcript into a highlight recap post for members who missed it.",
        deliverable: "a recap post ready to publish",
      },
      {
        q: "Draft answers to the 10 questions our members ask most, and format them as a community FAQ.",
        deliverable: "a formatted FAQ document",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own community manager work",
        body: "Founders hand off the repetitive community manager work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing community manager duties",
        body: "Small teams point CellCog AI Employee at the community manager work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running community manager work at scale",
        body: "Agencies produce community manager deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Personal connection",
        body: "Remembering a member's story and checking in personally stays with you. Your AI Community Manager reminds you who to reach out to.",
      },
      {
        title: "Real conflict",
        body: "When tensions flare between members, you step in. Your AI Community Manager flags the situation early and drafts a calm response.",
      },
      {
        title: "The face of the community",
        body: "People follow people, and that face is yours. Your AI Community Manager keeps the programming running behind you.",
      },
    ],
    comparison3: {
      title: "Three ways to run your community",
      cols: ["Doing it yourself", "A community tool", "AI Community Manager"],
      rows: [
        { label: "Sentiment read", cells: ["What you can keep up with", "Activity stats", "A week of messages summarized"] },
        { label: "Content programming", cells: ["Dips when busy", "Schedules what you made", "Plans and drafts the posts"] },
        { label: "Response drafting", cells: ["One reply at a time", "No", "Drafts answers and FAQs in bulk"] },
        { label: "Asks before posting", cells: ["n/a", "n/a", "Yes, by default"] },
      ],
    },
    faq: [
      {
        q: "Can CellCog AI Employee really understand what my community is feeling?",
        a: "They read through your messages and surface themes, sentiment, and recurring frustrations with supporting quotes. They give you the read; you decide what to do with it.",
      },
      {
        q: "Can they post and reply in the community itself?",
        a: "They draft posts, replies, and announcements in your voice. With the right connection and your approval they can publish, but the personal, relationship-building replies are best kept with you.",
      },
      {
        q: "What happens when a member is upset or there is a conflict?",
        a: "Your AI Community Manager can flag the situation and draft a calm response, but you should handle real conflict directly. They are there to catch it early, not to replace your judgment in tense moments.",
      },
      {
        q: "Will the content feel generic or off-brand?",
        a: "Give them your past posts and brand voice and they match your style. The more context you provide, the more it sounds like your community rather than a template.",
      },
      {
        q: "How is this better than just reading the channel myself?",
        a: "You could, but across hundreds of messages a week it is slow. CellCog AI Employee summarizes the week in minutes so you spend your time engaging, not scrolling.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Community Manager.",
      },
    ],
    ctaBand: {
      title: "Put your AI Community Manager to work today",
      body: "Paste in a week of community messages and get back a clear read on what your members care about.",
    },
  },
  "ai-legal-assistant": {
    slug: "ai-legal-assistant",
    roleDisplay: "AI Legal Assistant",
    roleNoun: "legal assistant",
    article: "a",
    seo: {
      title: "AI Legal Assistant: Hire a CellCog AI Employee | CellCog",
      description: "AI Legal Assistant from CellCog AI Employee: summarize contracts, draft documents, and organize case files on demand. See prompts and an honest comparison.",
    },
    hook: "A CellCog AI Employee summarizes contracts, flags key terms, dates, and obligations, and drafts routine documents from templates.",
    capabilities: [
      { title: "Summarize contracts", body: "Summarize contracts and flag key terms, dates, and obligations." },
      { title: "Draft routine documents", body: "Draft routine documents from templates (NDAs, engagement letters)." },
      { title: "Organize case files", body: "Organize and index case documents and exhibits." },
      { title: "Research legal topics", body: "Research legal topics and summarize findings in plain language." },
      { title: "Compare contract versions", body: "Compare two contract versions and highlight what changed." },
    ],
    prompts: [
      {
        q: "Summarize this 30-page vendor contract. Pull out payment terms, termination clauses, liability caps, renewal dates, and anything unusual I should flag before signing.",
        deliverable: "a plain-language contract summary with flagged terms",
      },
      {
        q: "Compare these two versions of an NDA and show me exactly what changed, clause by clause.",
        deliverable: "a redline comparison report",
      },
      {
        q: "Draft a standard mutual NDA from our template for a new vendor called Northwind, effective next Monday.",
        deliverable: "a ready-to-review NDA draft",
      },
      {
        q: "Build a timeline of events from these case documents with dates, parties, and key facts.",
        deliverable: "a chronological case timeline",
      },
      {
        q: "Research the basic requirements for forming an LLC in Delaware and summarize them in plain language.",
        deliverable: "a plain-language research summary",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own legal assistant work",
        body: "Founders hand off the repetitive legal assistant work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing legal assistant duties",
        body: "Small teams point CellCog AI Employee at the legal assistant work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running legal assistant work at scale",
        body: "Agencies produce legal assistant deliverables across many clients at once, each kept separate.",
      },
    ],
    limits: [
      {
        title: "Legal advice",
        body: "Only a licensed attorney gives legal advice. Your AI Legal Assistant prepares the work for your attorney and does not advise.",
      },
      {
        title: "Court appearances and filings",
        body: "Representing a client and signing filings stay with a qualified attorney.",
      },
      {
        title: "Negotiation",
        body: "Reading the other side and negotiating terms stays with you. Your AI Legal Assistant hands you the summary and the red flags.",
      },
    ],
    comparison3: {
      title: "Three ways to handle routine legal work",
      cols: ["Doing it yourself", "A contract tool", "AI Legal Assistant"],
      rows: [
        { label: "Contract review", cells: ["Hours per contract", "Clause search", "Key terms, dates, and red flags in minutes"] },
        { label: "Routine drafting", cells: ["One at a time", "Templates you fill in", "Drafts from your templates"] },
        { label: "Organizes case files", cells: ["When there is time", "No", "Yes, with a summary"] },
        { label: "Gives legal advice", cells: ["No", "No", "No, your attorney does"] },
      ],
    },
    faq: [
      {
        q: "Can CellCog AI Employee give me legal advice?",
        a: "No. They summarize documents, draft from templates, and organize case materials, but they do not give legal advice. A licensed attorney must review your AI Legal Assistant's output and make the legal calls.",
      },
      {
        q: "Can I trust their contract summaries?",
        a: "They reliably surface key terms, dates, and unusual clauses to speed up review. You should still read the flagged sections yourself, since your AI Legal Assistant is there to make review faster, not to replace a lawyer's judgment.",
      },
      {
        q: "What happens if they misread a clause?",
        a: "That is why you review their work. Treat their summary as a fast first pass that points you to what matters, then verify the clauses that affect your decision.",
      },
      {
        q: "Is my confidential document data secure?",
        a: "Your documents stay yours and are used only to do the task you asked for. You control what you upload and can remove it at any time.",
      },
      {
        q: "Can they draft documents I can actually use?",
        a: "They draft from your templates and standard forms, producing a clean first version. A qualified person should always review and finalize before anything is signed or filed.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Legal Assistant.",
      },
    ],
    ctaBand: {
      title: "Put your AI Legal Assistant to work today",
      body: "Give them a contract to summarize and get back the key terms, dates, and red flags in plain language.",
    },
  },
  "ai-receptionist": {
    slug: "ai-receptionist",
    relatedPosts: ["best-ai-receptionists", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Receptionist",
    roleNoun: "receptionist",
    article: "a",
    seo: {
      title: "AI Receptionist: Hire a CellCog AI Employee | CellCog",
      description: "AI Receptionist from CellCog AI Employee: answer customer questions, draft replies, and build FAQs on demand. See prompts and how they compare to hiring.",
    },
    hook: "A CellCog AI Employee answers common customer questions from your docs, and drafts email responses to incoming inquiries.",
    capabilities: [
      { title: "Answer customer questions", body: "Answer common customer questions from your docs and knowledge base." },
      { title: "Draft email responses", body: "Draft email responses to incoming inquiries." },
      { title: "Build FAQs", body: "Build and maintain an FAQ document." },
      { title: "Route inquiries", body: "Route and summarize incoming messages by priority." },
      { title: "Schedule appointments", body: "Schedule appointments and send confirmations." },
    ],
    prompts: [
      {
        q: "Here are 15 questions customers ask us most. Write clear, friendly answers based on our docs and format them as a customer-facing FAQ page.",
        deliverable: "a formatted FAQ document",
      },
      {
        q: "Draft polite email responses for these five common situations: refund request, hours inquiry, booking change, complaint, and partnership ask.",
        deliverable: "five reusable email templates",
      },
      {
        q: "Summarize these 30 incoming messages by priority and tell me which three need a same-day reply.",
        deliverable: "a prioritized inbox summary",
      },
      {
        q: "Create a simple intake form for new client inquiries with the fields we need to route them correctly.",
        deliverable: "a ready-to-use intake form",
      },
      {
        q: "Write appointment confirmation and reminder messages for our booking flow, friendly and on brand.",
        deliverable: "confirmation and reminder templates",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own receptionist work",
        body: "Founders hand off the repetitive receptionist work and get back finished deliverables, so they can focus on what only they can do.",
      },
      {
        badge: "Small team",
        title: "Sharing receptionist duties",
        body: "Small teams point CellCog AI Employee at the receptionist work no one owns, so output stays consistent week after week.",
      },
      {
        badge: "Agency / operator",
        title: "Running receptionist work at scale",
        body: "Agencies produce receptionist deliverables across many clients at once, growing capacity without adding headcount.",
      },
    ],
    limits: [
      {
        title: "A warm human greeting",
        body: "The first impression of a friendly person at the front desk sets a tone that an automated reply cannot fully match.",
      },
      {
        title: "Reading walk-in situations",
        body: "A human receptionist notices when a visitor is confused, upset, or a VIP and adjusts in the moment.",
      },
      {
        title: "Physical front-desk tasks",
        body: "Greeting guests, handling deliveries, and managing the physical space are things only a person on-site can do.",
      },
    ],
    faq: [
      {
        q: "Can CellCog AI Employee really answer customer questions accurately?",
        a: "When you connect your docs and policies, your AI Receptionist answers from your real information rather than guessing. You review the answers they draft, so accuracy stays under your control.",
      },
      {
        q: "Can they actually respond to emails, or just draft them?",
        a: "They draft responses in your tone, and with your inbox connected and your approval they can reply directly. Anything sensitive routes to you first.",
      },
      {
        q: "What happens when a question is something they do not know?",
        a: "They flag the message for you instead of inventing an answer. That way unusual or sensitive inquiries reach a human, while routine ones get handled fast.",
      },
      {
        q: "Will customers be able to tell your AI Receptionist is not a person?",
        a: "The responses are warm and on-brand when you give your AI Receptionist your voice, but the receptionist is there for routine inquiries, not for pretending to be a specific person. Use your AI Receptionist to keep responses fast and consistent.",
      },
      {
        q: "Can they handle phone calls?",
        a: "Their strength is written communication: email, chat, FAQs, and scripts. For live phone reception a human is still better, but your AI Receptionist can prepare the answer scripts that make calls faster.",
      },
      {
        q: "How does the cost compare to hiring a receptionist?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Receptionist.",
      },
    ],
    ctaBand: {
      title: "Put your AI Receptionist to work today",
      body: "Give them your most common customer questions and get back a clean, friendly FAQ you can publish today.",
    },
  },
  "ai-sdr": {
    slug: "ai-sdr",
    relatedPosts: ["best-ai-sdr-tools", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI SDR",
    roleNoun: "sales development representative",
    article: "an",
    seo: {
      title: "AI SDR: Hire a CellCog AI Employee | CellCog",
      description: "AI SDR from CellCog AI Employee: research leads, write genuinely personalized outreach, run follow-ups, and report on pipeline. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee researches your prospects, writes outreach that is personal because the employee actually did the research, follows up on schedule, and keeps your pipeline reporting current.",
    capabilities: [
      { title: "Research and build lead lists", body: "Build targeted lead lists from your ideal customer profile, researching each company and contact before anyone gets an email." },
      { title: "Write personalized outreach", body: "Write one-to-one outreach grounded in real research on each prospect, not a template with a first-name token." },
      { title: "Run outreach campaigns safely", body: "Send from dedicated sending domains with built-in volume caps, address verification, and suppression handling that protect your main domain's reputation." },
      { title: "Follow up on schedule", body: "Track every thread, follow up at the right interval, and stop the moment a prospect replies or opts out." },
      { title: "Report on pipeline", body: "Keep a live view of prospects contacted, replies, and meetings booked, with honest numbers you can check." },
    ],
    prompts: [
      {
        q: "Here is our ideal customer profile: B2B SaaS, 11 to 50 people, US-based, selling to finance teams. Build a list of 25 target companies with the right contact at each and a one-line reason they fit.",
        deliverable: "a researched lead list with fit notes",
      },
      {
        q: "Research these 10 prospects and write a personalized opening email for each, referencing something real about their company. Keep each under 90 words.",
        deliverable: "ten researched, individually written outreach drafts",
      },
      {
        q: "Design a 3-touch follow-up sequence for prospects who did not reply to the first email: timing, angle for each touch, and when to stop.",
        deliverable: "a follow-up playbook with draft copy",
      },
      {
        q: "Give me a pipeline report for this month: who we contacted, reply rate, positive replies, and which segments are responding best.",
        deliverable: "a pipeline report with segment breakdown",
      },
      {
        q: "We are up against a competitor in this deal. Build a one-page battlecard: their pricing, their strengths, where we win, and the three questions to ask the prospect.",
        deliverable: "a one-page competitive battlecard PDF",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own prospecting",
        body: "Founders hand off list building, research, and first-touch outreach, and spend their selling time only on prospects who replied.",
      },
      {
        badge: "Small team",
        title: "Adding outbound without an outbound hire",
        body: "Small teams get a steady outbound motion: researched lists, personal outreach, and disciplined follow-up, without adding headcount.",
      },
      {
        badge: "Agency / operator",
        title: "Running outreach across clients",
        body: "Agencies run researched, personalized outreach programs for many clients at once, each from its own sending domain.",
      },
    ],
    limits: [
      {
        title: "Live phone calls",
        body: "Cold calling and live discovery calls are human work. The AI SDR fills the calendar; a human takes the call.",
      },
      {
        title: "Complex negotiation",
        body: "Multi-stakeholder deals and pricing negotiations need a human account executive.",
      },
      {
        title: "In-person relationships",
        body: "Conferences, dinners, and handshakes still belong to people.",
      },
    ],
    faq: [
      {
        q: "Can they really send cold outreach on my behalf?",
        a: "Yes. Outreach goes through dedicated sending domains with pre-warmed mailboxes, address verification, daily volume caps, and automatic suppression of unsubscribes and bounces, so your main domain's reputation stays protected.",
      },
      {
        q: "Will the emails feel like spam?",
        a: "The model is quality over volume: the AI SDR researches each prospect and writes each email individually. Small, well-researched batches that a real person would be glad to receive outperform blasts, and the volume caps enforce that discipline.",
      },
      {
        q: "How do they find leads?",
        a: "You describe your ideal customer profile and connect a lead database if you use one. They source candidates, research each company, and only reach out to contacts that genuinely fit.",
      },
      {
        q: "What happens when a prospect replies?",
        a: "Follow-ups stop immediately and the reply lands in the AI SDR's inbox for a considered response. Live calls hand off to you, with the full thread and research notes attached. If you want the AI SDR in the meeting, add them to the Google Meet, Zoom or Microsoft Teams invite: they join under their own name and answer what they are authorized to, and never quote a price or a date in the room.",
      },
      {
        q: "How is this different from an AI SDR tool like 11x or Artisan?",
        a: "Those are dedicated sales platforms priced for enterprise sales teams. A CellCog AI Employee does the SDR work and everything around it: market research, battlecards, pipeline reporting, and content, at self-serve pricing.",
      },
      {
        q: "How does the cost compare to hiring an SDR?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI SDR.",
      },
    ],
    ctaBand: {
      title: "Put your AI SDR to work today",
      body: "Give them your ideal customer profile and get back a researched lead list with personalized outreach drafts.",
    },
  },
  "ai-customer-support-agent": {
    slug: "ai-customer-support-agent",
    roleDisplay: "AI Customer Support Agent",
    roleNoun: "customer support agent",
    article: "a",
    seo: {
      title: "AI Customer Support Agent: Hire a CellCog AI Employee | CellCog",
      description: "AI Customer Support Agent from CellCog AI Employee: answer support email, keep a knowledge base current, and report on ticket trends. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee answers support email with real product knowledge, keeps your help docs current, spots recurring issues, and reports the trends.",
    capabilities: [
      { title: "Answer support email", body: "Work the support inbox: read the full thread, answer with real product knowledge, and escalate what genuinely needs you." },
      { title: "Maintain the knowledge base", body: "Turn resolved tickets into help articles and keep existing docs accurate as the product changes." },
      { title: "Spot recurring issues", body: "Notice when the same problem keeps arriving, document the pattern, and flag it to the product owner with examples." },
      { title: "Report on support trends", body: "Track volume, response times, and top issues, and deliver a clear weekly report." },
      { title: "Draft saved replies", body: "Build and refine a library of high-quality reply templates for the issues that repeat." },
    ],
    prompts: [
      {
        q: "Here are 15 open support emails. Answer the ones you can from our docs, draft replies for the tricky ones for my review, and tell me which two need me personally.",
        deliverable: "answered tickets plus a triage summary",
      },
      {
        q: "Go through last month's resolved tickets and write help-center articles for the five most common questions.",
        deliverable: "five publish-ready help articles",
      },
      {
        q: "Our refund policy changed. Find every help article and saved reply that mentions the old policy and update them.",
        deliverable: "updated docs with a change log",
      },
      {
        q: "Build a weekly support report: ticket volume, average first-response time, top five issues, and anything unusual.",
        deliverable: "a weekly support report",
      },
      {
        q: "This customer is frustrated after three back-and-forths. Read the whole thread and draft a reply that actually resolves it, plus a note on what went wrong.",
        deliverable: "a considered reply draft and an internal postmortem note",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own support",
        body: "Founders stop being the support team: the inbox gets worked every day, and only the conversations that truly need them reach them.",
      },
      {
        badge: "Small team",
        title: "Support that keeps up",
        body: "Small teams get consistent response times and a living knowledge base without pulling engineers off product work.",
      },
      {
        badge: "Agency / operator",
        title: "Support across client products",
        body: "Agencies keep support running for multiple client products at once, each with its own docs and voice.",
      },
    ],
    limits: [
      {
        title: "Angry escalations",
        body: "A genuinely upset customer may need you, and the goodwill call is yours. Your AI Customer Support Agent escalates with the full thread.",
      },
      {
        title: "Refunds and credits",
        body: "Money back to a customer waits for your yes. Your AI Customer Support Agent drafts the reply and the refund note.",
      },
      {
        title: "Policy exceptions",
        body: "Deciding when to break your own rules for a customer is a judgment call that stays with you.",
      },
    ],
    comparison3: {
      title: "Three ways to answer support",
      cols: ["Doing it yourself", "A support chatbot", "AI Customer Support Agent"],
      rows: [
        { label: "Answers support email", cells: ["Between everything else", "Scripted replies", "Reads the thread, answers with product knowledge"] },
        { label: "Keeps help docs current", cells: ["Falls behind", "No", "Yes, as part of the routine"] },
        { label: "Spots recurring issues", cells: ["When someone notices", "No", "A weekly report, with numbers"] },
        { label: "Escalates to you", cells: ["n/a", "When it gets stuck", "With the full thread and a draft"] },
      ],
    },
    faq: [
      {
        q: "Can they really answer customers on their own?",
        a: "Yes, within the boundaries you set. They answer from your docs and past tickets, route sensitive cases to you, and you can review their replies before they go out until you trust them to send directly.",
      },
      {
        q: "What if they do not know the answer?",
        a: "They say so and escalate. They work from your connected docs and history, so unanswerable questions become flags for you and, over time, new help articles.",
      },
      {
        q: "Will customers know they are talking to an AI?",
        a: "That is your call. Your AI Customer Support Agent works under their own name and can be transparent about being an AI employee. Honesty tends to work well when the answers are genuinely good.",
      },
      {
        q: "How do they learn our product?",
        a: "You connect your docs, past tickets, and product resources. Your AI Customer Support Agent builds and maintains their own knowledge over time, and every resolved ticket makes them better.",
      },
      {
        q: "How is this different from a support chatbot?",
        a: "A chatbot deflects with canned answers. A CellCog AI Employee works the actual inbox, writes docs, spots product issues, and reports trends, the whole job around the replies.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Customer Support Agent.",
      },
    ],
    ctaBand: {
      title: "Put your AI Customer Support Agent to work today",
      body: "Hand them your ten most recent tickets and get back drafted replies plus the help article that prevents the next ten.",
    },
  },
  "ai-data-analyst": {
    slug: "ai-data-analyst",
    relatedPosts: ["best-ai-data-analysts", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Data Analyst",
    roleNoun: "data analyst",
    article: "a",
    seo: {
      title: "AI Data Analyst: Hire a CellCog AI Employee | CellCog",
      description: "AI Data Analyst from CellCog AI Employee: query your data, build live dashboards, and deliver analysis you can act on. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee queries your databases, builds live dashboards, finds the story in your numbers, and delivers analysis you can act on.",
    capabilities: [
      { title: "Query your data directly", body: "Connect databases, spreadsheets, and analytics tools; your AI Data Analyst writes and runs the queries." },
      { title: "Build live dashboards", body: "Build interactive dashboards that stay current, refreshed on a schedule as part of your AI Data Analyst's routine." },
      { title: "Find the story in the numbers", body: "Go beyond charts: segment, compare cohorts, and explain what changed and why it matters." },
      { title: "Deliver decision-ready reports", body: "Turn analysis into clear reports, spreadsheets with real formulas, and presentation-ready documents." },
      { title: "Monitor metrics over time", body: "Watch your key metrics shift to shift and flag meaningful changes before they become surprises." },
    ],
    prompts: [
      {
        q: "Connect to our database and tell me: which customer segments grew last quarter, which shrank, and what changed. Build a dashboard I can check every week.",
        deliverable: "a segment analysis plus a live dashboard",
      },
      {
        q: "Here is a CSV of 12 months of sales. Find seasonality, our top products by margin, and three things we should do differently.",
        deliverable: "an analysis report with recommendations",
      },
      {
        q: "Our churn ticked up in May. Dig into the data and tell me which cohorts are churning, when in their lifecycle, and any common patterns.",
        deliverable: "a churn investigation with cohort breakdowns",
      },
      {
        q: "Build a financial model in a spreadsheet: revenue projection for the next 6 months based on our actuals, with assumptions I can adjust.",
        deliverable: "a spreadsheet model with live formulas",
      },
      {
        q: "Every Monday, refresh our KPI dashboard from the database and email me a summary of what moved and why.",
        deliverable: "a recurring dashboard refresh with a weekly summary",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own analysis",
        body: "Founders stop exporting CSVs at midnight: the numbers arrive analyzed, with the so-what attached.",
      },
      {
        badge: "Small team",
        title: "Analytics without an analytics hire",
        body: "Small teams get real dashboards and honest metric readouts without borrowing engineering time.",
      },
      {
        badge: "Agency / operator",
        title: "Reporting across clients",
        body: "Agencies deliver polished, data-backed reports for every client on a schedule, without a reporting team.",
      },
    ],
    limits: [
      {
        title: "Domain intuition",
        body: "Knowing which anomaly is a data glitch and which is a business crisis often takes lived context.",
      },
      {
        title: "Political questions",
        body: "Deciding which numbers to show the board, and how, is a human call.",
      },
      {
        title: "Data you have not connected",
        body: "They analyze what they can reach. Tribal knowledge in someone's head stays out of scope until written down.",
      },
    ],
    faq: [
      {
        q: "Can they really connect to our database?",
        a: "Yes, with credentials you control, and read-only access is the sensible default. They write their own queries, and you decide what they can reach and can disconnect anytime.",
      },
      {
        q: "What data tools do they work with?",
        a: "Databases like MongoDB and PostgreSQL, spreadsheets, CSVs, Google Analytics, and hundreds of connected apps. If your data is reachable, your AI Data Analyst can usually work with it.",
      },
      {
        q: "How do I know their analysis is right?",
        a: "They show their work: queries, assumptions, and methodology come with every analysis, so you can verify rather than trust. Computed values come from real code, not estimation.",
      },
      {
        q: "Can they build dashboards our whole team uses?",
        a: "Yes. They build interactive web dashboards they keep current on a schedule, and you can share them with your team.",
      },
      {
        q: "How is this different from a BI tool like Looker or Tableau?",
        a: "BI tools visualize data after someone models it and builds the views. A CellCog AI Employee does that work themselves: connects, queries, models, builds the dashboard, and explains what the numbers mean.",
      },
      {
        q: "How does the cost compare to hiring a data analyst?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Data Analyst.",
      },
    ],
    ctaBand: {
      title: "Put your AI Data Analyst to work today",
      body: "Point them at last quarter's data and get back the analysis, the dashboard, and the three decisions hiding in your numbers.",
    },
  },
  "ai-bookkeeper": {
    slug: "ai-bookkeeper",
    relatedPosts: ["best-ai-bookkeeping-software", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Bookkeeper",
    roleNoun: "bookkeeper",
    article: "a",
    seo: {
      title: "AI Bookkeeper: Hire a CellCog AI Employee | CellCog",
      description: "AI Bookkeeper from CellCog AI Employee: categorize transactions, reconcile accounts, run month-end close, and keep books audit-ready. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee categorizes your transactions, reconciles every account, chases overdue invoices, and closes your books on schedule.",
    capabilities: [
      { title: "Categorize and reconcile", body: "Categorize transactions against your chart of accounts and reconcile bank and card statements line by line." },
      { title: "Track invoices and bills", body: "Track receivables and payables, flag what is overdue, and prepare payment runs for your approval." },
      { title: "Close the books monthly", body: "Run a disciplined month-end close: reconciliations, an accruals checklist, and a close report you can hand to your accountant." },
      { title: "Prepare financial reports", body: "Prepare P&L, cash flow, and balance sheet summaries in plain language, with the anomalies called out." },
      { title: "Keep records audit-ready", body: "Maintain clean, organized records with a paper trail, so tax season stops being an emergency." },
    ],
    prompts: [
      {
        q: "Here are last month's bank and credit card statements plus our chart of accounts. Categorize every transaction and flag anything you are unsure about.",
        deliverable: "a categorized ledger with flagged exceptions",
      },
      {
        q: "Reconcile our checking account against the books for June and list every discrepancy with your best explanation.",
        deliverable: "a reconciliation report with discrepancy notes",
      },
      {
        q: "Build an accounts receivable aging report and draft polite reminder emails for every invoice more than 30 days overdue.",
        deliverable: "an AR aging report with ready-to-send reminders",
      },
      {
        q: "Run our month-end close checklist and give me a close report: what is reconciled, what is accrued, and what needs my decision.",
        deliverable: "a month-end close report",
      },
      {
        q: "Prepare a monthly financial summary for my accountant: P&L, cash position, and notable changes versus last month.",
        deliverable: "an accountant-ready financial package",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Doing their own books",
        body: "Founders hand off categorization, reconciliation, and invoice tracking, and see their real numbers weekly instead of at tax time.",
      },
      {
        badge: "Small team",
        title: "Clean books without a bookkeeping hire",
        body: "Small teams keep current books and a disciplined month-end close without adding headcount.",
      },
      {
        badge: "Agency / operator",
        title: "Bookkeeping across entities",
        body: "Operators keep separate, organized books for multiple entities or clients, each with its own close rhythm.",
      },
    ],
    limits: [
      {
        title: "Filing taxes",
        body: "Tax returns and filings belong to a licensed CPA or tax professional. The AI Bookkeeper prepares clean books that make that work fast.",
      },
      {
        title: "Audits and attestation",
        body: "Anything requiring a license or a signature, reviews, audits, attestation, stays with credentialed humans.",
      },
      {
        title: "Judgment calls with tax consequences",
        body: "Ambiguous classifications that affect your taxes get flagged for you or your accountant, not guessed.",
      },
    ],
    faq: [
      {
        q: "Which accounting tools do they work with?",
        a: "They connect to QuickBooks and work from your bank exports. If your books live in spreadsheets today, your AI Bookkeeper works directly with those, and can help you move to proper software when you are ready.",
      },
      {
        q: "Is it safe to give them access to our financials?",
        a: "You control access and approvals. They can work read-only, and any action you mark as sensitive, like sending an invoice or preparing a payment, waits for your explicit approval. Every action they take is logged and reviewable.",
      },
      {
        q: "What happens when they are unsure how to categorize something?",
        a: "They flag the transaction with their best guess and the reason they are unsure, then ask you or your accountant to decide. Your answer becomes a rule your AI Bookkeeper remembers, so the same question does not come back next month.",
      },
      {
        q: "Can they chase overdue invoices?",
        a: "Yes. They track receivables, draft polite reminders in your voice, and escalate on a schedule you set. You can require approval on every email or let them handle routine reminders on their own.",
      },
      {
        q: "Do I still need an accountant?",
        a: "For taxes, filings, and formal advice, yes. The AI Bookkeeper handles the daily and monthly work, categorization, reconciliation, close, and hands your accountant clean books. Most customers keep a CPA they see quarterly and skip the monthly bookkeeping spend.",
      },
      {
        q: "How does the cost compare to hiring a bookkeeper?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Bookkeeper.",
      },
    ],
    ctaBand: {
      title: "Put your AI Bookkeeper to work today",
      body: "Hand them last month's statements and get back categorized books, a reconciliation report, and a short list of what needs your eyes.",
    },
  },
  "ai-recruiter": {
    slug: "ai-recruiter",
    relatedPosts: ["best-ai-recruiters", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Recruiter",
    roleNoun: "recruiter",
    article: "a",
    seo: {
      title: "AI Recruiter: Hire a CellCog AI Employee | CellCog",
      description: "AI Recruiter from CellCog AI Employee: source candidates, screen applications with written rationale, write personalized outreach, and keep your pipeline moving. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee sources candidates, screens every application against your real requirements, writes outreach people actually answer, and keeps your pipeline moving.",
    capabilities: [
      { title: "Source candidates", body: "Search for candidates matching your role requirements and build ranked shortlists with fit notes on every name." },
      { title: "Screen applications", body: "Screen every application against the actual job requirements, with a written rationale for each yes or no." },
      { title: "Write personalized outreach", body: "Write candidate outreach grounded in each person's real background, not a template with a name token." },
      { title: "Coordinate interviews", body: "Schedule interviews, brief the interviewers, and keep candidates informed at every stage." },
      { title: "Report on the pipeline", body: "Keep your hiring tracker current and report on pipeline health, stage conversion, and time-to-fill." },
    ],
    prompts: [
      {
        q: "Here is the job description for our senior backend engineer role. Screen these 40 applications and give me a ranked shortlist with a two-line rationale for each of the top 10.",
        deliverable: "a ranked, reasoned candidate shortlist",
      },
      {
        q: "Source 20 candidates for our head of customer success role: SaaS background, 5+ years, US time zones. Include why each one fits.",
        deliverable: "a sourced candidate list with fit notes",
      },
      {
        q: "Write personalized outreach to these 10 candidates, referencing something real from their background. Keep each under 120 words.",
        deliverable: "ten individually written outreach drafts",
      },
      {
        q: "Draft a structured interview guide for this role: the competencies, questions for each, and what a strong answer sounds like.",
        deliverable: "a structured interview guide",
      },
      {
        q: "Give me a pipeline report: candidates per stage, conversion rates, where candidates drop off, and time-to-fill against benchmark.",
        deliverable: "a hiring pipeline report",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Hiring without a talent team",
        body: "Founders hand off sourcing, screening, and scheduling, and spend their hiring time only on candidates worth meeting.",
      },
      {
        badge: "Small team",
        title: "A recruiting function without a recruiter",
        body: "Small teams run real hiring processes: sourced pipelines, consistent screening, and candidates who never go cold.",
      },
      {
        badge: "Agency / operator",
        title: "Running searches across clients",
        body: "Recruiting agencies and fractional talent partners run more searches in parallel, with research-grade screening on every one.",
      },
    ],
    limits: [
      {
        title: "The hiring decision",
        body: "Who to hire is a human call. The AI Recruiter builds the shortlist and the evidence; you make the decision.",
      },
      {
        title: "Closing senior candidates",
        body: "Selling an executive or a hesitant senior candidate on the role takes a human relationship.",
      },
      {
        title: "Fair-hiring policy",
        body: "They apply the criteria you set and show their reasoning, but your team owns hiring policy and compliance.",
      },
    ],
    faq: [
      {
        q: "Can they work with our ATS?",
        a: "They connect to over 1,000 business tools, including common ATS and HR platforms. If you hire from a spreadsheet and an inbox, they work with that too, and will keep it more organized than it has ever been.",
      },
      {
        q: "Will candidates know they are talking to an AI?",
        a: "That is your call, and honesty works best. The AI Recruiter can identify themselves as your recruiting assistant. The AI Recruiter's outreach reads well because they actually researched the candidate, which candidates notice far more than who signed the email.",
      },
      {
        q: "How do they keep screening consistent?",
        a: "They screen every application against the same criteria you define and write down their reasoning for each decision. That is more consistent and more auditable than a tired human skimming resumes, and you can review any decision the AI Recruiter made.",
      },
      {
        q: "Can they handle interview scheduling?",
        a: "Yes. They coordinate calendars, propose times, send invites and interviewer briefs, and reschedule when things move. Candidates get responses in minutes, not days.",
      },
      {
        q: "How is this different from an AI recruiting tool?",
        a: "Recruiting tools automate one step: sourcing, or screening, or scheduling. A CellCog AI Employee owns the whole workflow and everything around it: market research, offer benchmarking, interview guides, and pipeline reporting.",
      },
      {
        q: "How does the cost compare to hiring a recruiter?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Recruiter.",
      },
    ],
    ctaBand: {
      title: "Put your AI Recruiter to work today",
      body: "Give them a job description and get back a sourced shortlist with fit notes and outreach drafts.",
    },
  },
  "ai-virtual-assistant": {
    slug: "ai-virtual-assistant",
    relatedPosts: ["best-ai-virtual-assistants", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Virtual Assistant",
    roleNoun: "virtual assistant",
    article: "a",
    seo: {
      title: "AI Virtual Assistant: Hire a CellCog AI Employee | CellCog",
      description: "AI Virtual Assistant from CellCog AI Employee: inbox triage, scheduling, research, documents, and travel planning, with memory of how you like things done that carries from task to task. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee manages the inbox, the calendar, the research, and the follow-ups, works any hour you need, and never splits their attention across other clients.",
    capabilities: [
      { title: "Manage email and follow-ups", body: "Triage the inbox, draft replies in your voice, and make sure nothing that needs an answer goes quiet." },
      { title: "Keep the calendar sane", body: "Schedule meetings, resolve conflicts across time zones, and protect your focus time." },
      { title: "Run research and digital errands", body: "Compare vendors, find the best option, fill out the forms, and come back with a recommendation, not a list of links." },
      { title: "Handle documents and data entry", body: "Draft, format, and organize documents, keep trackers current, and move data between systems without transcription errors." },
      { title: "Plan travel and logistics", body: "Plan trips end to end: flights, hotels, day-by-day itineraries, and the confirmations filed where you can find them." },
    ],
    prompts: [
      {
        q: "Go through my inbox from the last week. Draft replies for everything that needs one, flag the three things that actually need my attention, and archive the noise.",
        deliverable: "a triaged inbox with reply drafts",
      },
      {
        q: "Find the best project management tool for a 6-person team under $50 a month. Compare the top options and recommend one.",
        deliverable: "a vendor comparison with a recommendation",
      },
      {
        q: "Plan a 3-day trip to Austin for the conference: flights from JFK, a hotel near the venue, and a day-by-day itinerary.",
        deliverable: "a complete travel plan",
      },
      {
        q: "Turn these meeting notes into a clean summary, a decision log, and a follow-up email to the team.",
        deliverable: "meeting notes turned into action items",
      },
      {
        q: "Build a tracker for our vendor contracts: renewal dates, costs, and owners, and remind me 30 days before each renewal.",
        deliverable: "a contract tracker with automatic reminders",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Getting their week back",
        body: "Founders hand off the inbox, the scheduling, and the digital errands, and get back the hours they were spending on administration.",
      },
      {
        badge: "Small team",
        title: "Shared support for the whole team",
        body: "Small teams share one assistant that handles scheduling, research, documents, and follow-ups for everyone at once.",
      },
      {
        badge: "Agency / operator",
        title: "An assistant on every account",
        body: "Operators give each client account its own assistant workflows: trackers kept current, reports on schedule, inboxes answered.",
      },
    ],
    limits: [
      {
        title: "Physical-world errands",
        body: "Picking up packages and being somewhere in person still need a human. Anything digital is fair game.",
      },
      {
        title: "Personal judgment calls",
        body: "They will draft the difficult email; whether to send it is your call.",
      },
      {
        title: "Phone calls",
        body: "They do not dial phones. They can join Google Meet, Zoom and Microsoft Teams under their own name, and either way they handle the prep, the notes and the follow-up.",
      },
    ],
    faq: [
      {
        q: "What can an AI virtual assistant actually do?",
        a: "Everything a virtual assistant does on a computer: email triage and drafting, scheduling, research, data entry, document preparation, travel planning, and follow-ups. Your AI Virtual Assistant also does things most VAs cannot, like building spreadsheets with working formulas, live dashboards, and presentation decks.",
      },
      {
        q: "How is this different from hiring a human VA?",
        a: "A human VA works set hours, splits attention across clients, and takes their knowledge with them when they leave. A CellCog AI Employee works any hour, carries your preferences and processes from shift to shift, and costs a fraction of a full-time hire.",
      },
      {
        q: "How is this different from ChatGPT?",
        a: "ChatGPT answers when you prompt it and forgets you between sessions. A CellCog AI Employee is a standing worker: they have their own inbox, channels, and task board, don't reset between sessions, start work on schedules without being prompted, and own outcomes rather than answering questions.",
      },
      {
        q: "Can I trust them with my inbox and calendar?",
        a: "You choose the access level and the approval rules. Many owners start read-only with draft-for-approval, then expand what your AI Virtual Assistant can do on their own as trust builds. Every action is logged.",
      },
      {
        q: "Do they learn how I like things done?",
        a: "Yes. They keep durable memory across every task and conversation: your preferences, your voice, your standing rules. They get noticeably better in the first weeks, and what they learn in one shift carries into the next.",
      },
      {
        q: "Can they join my video calls?",
        a: "Yes. Add their email to the invite and they join Google Meet, Zoom or Microsoft Teams under their own name, camera off. They listen, answer when addressed within what you have authorized, and never commit you to anything in the call. The notes and action items land on their task board afterward.",
      },
      {
        q: "How does the cost compare to hiring a virtual assistant?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Virtual Assistant.",
      },
    ],
    ctaBand: {
      title: "Put your AI Virtual Assistant to work today",
      body: "Hand them your inbox and your calendar and get back a triaged week, drafted replies, and a schedule that finally makes sense.",
    },
  },
  "ai-operations-manager": {
    slug: "ai-operations-manager",
    roleDisplay: "AI Operations Manager",
    roleNoun: "operations manager",
    article: "an",
    seo: {
      title: "AI Operations Manager: Hire a CellCog AI Employee | CellCog",
      description: "AI Operations Manager from CellCog AI Employee: document processes, run recurring reports, track KPIs on live dashboards, and flag problems early. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee documents your processes, watches your metrics, runs the recurring operational work, and flags problems while they are still small.",
    capabilities: [
      { title: "Document and improve processes", body: "Turn how things actually get done into clear SOPs, then keep them current as the process evolves." },
      { title: "Run recurring operations", body: "Own the weekly and monthly operational rhythm: reports generated, checklists run, follow-ups sent, on schedule." },
      { title: "Track KPIs and flag anomalies", body: "Watch your operational metrics on live dashboards and raise the flag when a number moves the wrong way." },
      { title: "Coordinate across tools and teams", body: "Keep systems in sync and stakeholders informed: statuses updated, handoffs tracked, nothing dropped between tools." },
      { title: "Analyze bottlenecks", body: "Dig into where work slows down, quantify what it costs, and propose fixes with the data to back them.", },
    ],
    prompts: [
      {
        q: "Interview me about how we onboard a new customer, then write the SOP: steps, owners, timing, and a checklist version.",
        deliverable: "a complete onboarding SOP",
      },
      {
        q: "Build a weekly operations report that pulls our order volume, fulfillment time, and support backlog, and send it every Monday morning.",
        deliverable: "a recurring weekly ops report",
      },
      {
        q: "Our fulfillment time went from 2 days to 5 days last month. Dig into the data and tell me where the delay is coming from.",
        deliverable: "a bottleneck analysis with a fix proposal",
      },
      {
        q: "Build a live dashboard of our top 8 operational metrics and keep it current daily.",
        deliverable: "a live operations dashboard",
      },
      {
        q: "Audit our software subscriptions and vendor contracts: what we pay, what we use, and what we should cancel or renegotiate.",
        deliverable: "a vendor spend audit",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Operations that run on schedule",
        body: "Founders get the operational rhythm of a bigger company: documented processes, weekly reports, and metrics watched daily.",
      },
      {
        badge: "Small team",
        title: "The glue between tools and people",
        body: "Small teams stop dropping handoffs: statuses stay current, reports arrive on schedule, and problems surface early.",
      },
      {
        badge: "Agency / operator",
        title: "Standardizing across accounts",
        body: "Operators run the same disciplined playbook across every client or entity, with per-account dashboards and reporting.",
      },
    ],
    limits: [
      {
        title: "Managing people",
        body: "Performance conversations, hiring, and team leadership stay with you. Your AI Operations Manager gives you the data and the time to do them well.",
      },
      {
        title: "Physical operations",
        body: "Warehouse walks, site visits, and vendor lunches still need you there in person.",
      },
      {
        title: "Big structural calls",
        body: "Reorganizations and major vendor switches are your decisions. Your AI Operations Manager prepares the analysis that makes them easier.",
      },
    ],
    comparison3: {
      title: "Three ways to run operations",
      cols: ["Doing it yourself", "A workflow tool", "AI Operations Manager"],
      rows: [
        { label: "Process documentation", cells: ["Out of date", "Steps you configure", "SOPs kept current as things change"] },
        { label: "Metric monitoring", cells: ["Weekly at best", "Alerts you set up", "Every day, anomalies explained"] },
        { label: "Recurring operations", cells: ["You, by hand", "Automations you build", "Runs them and reports back"] },
        { label: "Reporting", cells: ["When there is time", "Dashboards you read", "On schedule, every time"] },
      ],
    },
    faq: [
      {
        q: "What does an AI operations manager do day to day?",
        a: "They work on a schedule: check the metrics, run the recurring reports and checklists, update trackers, chase open loops, and flag anything unusual. Between scheduled runs they pick up the operational tasks you assign, from process documentation to vendor research.",
      },
      {
        q: "Can they work with our existing tools?",
        a: "They connect to over 1,000 business tools including Slack, Notion, Airtable, spreadsheets, and most CRMs and project trackers. Your AI Operations Manager can also work directly with your databases and internal systems, with exactly the access you choose.",
      },
      {
        q: "How do they learn our processes?",
        a: "You explain a process once, in plain language, and they write the SOP and remember it permanently. Then they improve the documentation as they actually run the process and hit the real-world edge cases.",
      },
      {
        q: "Will they make changes without asking?",
        a: "Only where you allow it. You define which actions are routine and which require your approval, and they request sign-off on anything you mark sensitive. Every action is logged.",
      },
      {
        q: "How is this different from workflow automation like Zapier?",
        a: "Automation runs fixed triggers between apps and breaks when reality changes. An operations manager reasons: they notice when something looks wrong, investigate, adapt the process, and tell you what they changed and why.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Operations Manager.",
      },
    ],
    ctaBand: {
      title: "Put your AI Operations Manager to work today",
      body: "Describe one messy process and get back the SOP, the checklist, and a dashboard tracking how it performs.",
    },
  },
  "ai-software-engineer": {
    slug: "ai-software-engineer",
    roleDisplay: "AI Software Engineer",
    roleNoun: "software engineer",
    article: "a",
    seo: {
      title: "AI Software Engineer: Hire a CellCog AI Employee | CellCog",
      description: "Hire an AI engineer as a CellCog AI Employee: ships features with tests, fixes bugs, reviews pull requests, and keeps your codebase documented. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee ships features end to end, fixes bugs with regression tests, reviews pull requests, and keeps your codebase documented, working directly in your repositories.",
    capabilities: [
      { title: "Ship features end to end", body: "Take a feature from requirement to working code: implementation, tests, and a clean diff ready for your review." },
      { title: "Fix bugs with tests", body: "Reproduce the bug, trace the root cause, ship the fix with a regression test and a written explanation." },
      { title: "Review pull requests", body: "Review code against your standards and catch what could break in production, with reasoning on every comment." },
      { title: "Keep the codebase documented", body: "Keep architecture docs, onboarding guides, and API references current as the code actually evolves." },
      { title: "Automate the maintenance", body: "Write the scripts, CI checks, and migrations, and patch the small problems before they grow into incidents." },
    ],
    prompts: [
      {
        q: "Here's a customer bug report: uploads over 50MB fail silently. Find the root cause, fix it, and add a test that would have caught it.",
        deliverable: "a fix with a regression test and a root-cause note",
      },
      {
        q: "Build the CSV export feature from this spec: filters, column selection, and email delivery for large files.",
        deliverable: "a working feature with tests, ready for review",
      },
      {
        q: "Review this pull request against our style guide and flag anything that could break in production.",
        deliverable: "a line-by-line review with written reasoning",
      },
      {
        q: "Our onboarding doc is a year out of date. Read the codebase and rewrite it so a new engineer can ship in week one.",
        deliverable: "a current onboarding guide",
      },
      {
        q: "The dashboard API takes 6 seconds to load. Profile it, find the slow queries, and fix them.",
        deliverable: "a performance fix with before-and-after numbers",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "Shipping features from day one",
        body: "Non-technical founders ship real features and fixes. Technical founders hand off the backlog they never get to.",
      },
      {
        badge: "Small team",
        title: "Clearing the backlog",
        body: "Small teams point a CellCog AI Employee at the bug queue, the tests, and the docs, so your team stays on the hard problems.",
      },
      {
        badge: "Agency / operator",
        title: "Maintaining many codebases at once",
        body: "Agencies and dev shops keep every client project patched, tested, and documented in parallel.",
      },
    ],
    limits: [
      {
        title: "Architecture ownership",
        body: "System-design calls with long-term consequences stay yours. Your AI Software Engineer prepares the analysis and the options.",
      },
      {
        title: "Production judgment",
        body: "You decide what deploys and when. They work in your review flow, and you hold the merge button for as long as you want.",
      },
      {
        title: "Tribal knowledge",
        body: "Code that only makes sense with context nobody wrote down takes longer at first. They document that knowledge as they learn it, so the problem shrinks.",
      },
    ],
    comparison3: {
      title: "Three ways to get engineering work done",
      cols: ["Doing it yourself", "A coding assistant", "AI Software Engineer"],
      rows: [
        { label: "Takes a ticket end to end", cells: ["You", "Helps while you code", "Plans, codes, tests, returns a diff"] },
        { label: "Code review", cells: ["Hours to days", "On request", "Minutes, with written reasoning"] },
        { label: "Tests", cells: ["When there is time", "If you ask", "A test with every fix"] },
        { label: "Documentation", cells: ["Behind", "If you ask", "Kept current as the code changes"] },
        { label: "Waits for your merge", cells: ["n/a", "n/a", "Yes, by default"] },
      ],
    },
    faq: [
      {
        q: "Can an AI engineer really work in our codebase?",
        a: "Yes. They work directly in your repositories, through your own machine or their cloud computer: they read the code, follow your conventions, run your tests, and deliver changes as clean diffs for your review. CellCog's own production codebase is built and maintained this way.",
      },
      {
        q: "What languages and stacks do they support?",
        a: "The common production stacks: Python, JavaScript and TypeScript, React, SQL, and the usual cloud and CI tooling, plus most mainstream languages. If your stack is unusual, they read the existing code first and follow the patterns already there rather than imposing new ones.",
      },
      {
        q: "Will they break things in production?",
        a: "They work carefully: changes land as reviewable diffs, tests run before anything ships, and you decide which actions they can take on their own versus which wait for your sign-off. Every action is logged, so you can always see exactly what they did and why.",
      },
      {
        q: "How is this different from Copilot or Cursor?",
        a: "Coding assistants autocomplete while you type. A CellCog AI Employee is the one doing the work: they take a ticket, plan the change, write the code and the tests, and come back with the diff. They also own everything around the code: reviews, documentation, and release notes.",
      },
      {
        q: "Can they work alongside our existing engineers?",
        a: "Yes, that is the most common setup. Engineers hand them the work that crowds out feature development: bug queues, test coverage, refactors, documentation, dependency updates. Your AI Software Engineer communicates in the tools your team already uses and leaves a written trail on everything they touch.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Software Engineer.",
      },
    ],
    ctaBand: {
      title: "Put your AI Software Engineer to work today",
      body: "Hand them a bug or a feature spec and get back a clean diff with tests, ready for your review.",
    },
  },
  "ai-designer": {
    slug: "ai-designer",
    roleDisplay: "AI Designer",
    roleNoun: "designer",
    article: "a",
    seo: {
      title: "AI Designer: Hire a CellCog AI Employee | CellCog",
      description: "Hire an AI Designer as a CellCog AI Employee: brand assets, marketing graphics, social visuals, icons, and decks, on brand every time. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee takes a brief, designs your brand assets, marketing graphics, social visuals, and decks, and keeps every piece in your visual language.",
    capabilities: [
      { title: "Produce brand and marketing assets", body: "Social graphics, ad creative, banners, and one-pagers, produced in your brand system, in every size you need." },
      { title: "Design presentation decks", body: "Turn a rough outline into a designed deck: layout, hierarchy, charts, and consistent styling throughout." },
      { title: "Keep everything on brand", body: "Learn your brand kit once, colors, type, logo rules, tone, and apply it to every asset without drifting." },
      { title: "Create illustrations and icons", body: "Original illustrations, icon sets, and diagrams in a consistent style, delivered in the formats you use." },
      { title: "Iterate fast on feedback", body: "Give notes in plain language and get revisions back in minutes, with version history kept organized." },
    ],
    prompts: [
      {
        q: "Here's our brand kit. Design 10 social graphics for our product launch: announcement, three feature highlights, quotes, and a countdown.",
        deliverable: "a launch graphics set in your brand system",
      },
      {
        q: "Turn this 12-slide outline into a designed investor deck with our fonts and colors.",
        deliverable: "a fully designed deck",
      },
      {
        q: "Design an icon set for the 8 features on our pricing page, one consistent style.",
        deliverable: "a consistent icon set in SVG",
      },
      {
        q: "Our landing page hero looks dated. Propose three visual directions with mockups.",
        deliverable: "three designed directions to choose from",
      },
      {
        q: "Take this case study and design it as a two-page PDF our sales team can send.",
        deliverable: "a designed case-study PDF",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "A design function from day one",
        body: "Founders get launch graphics, decks, and a consistent visual identity from day one.",
      },
      {
        badge: "Small team",
        title: "No more waiting on design",
        body: "Marketing and product get assets back the same day, on brand.",
      },
      {
        badge: "Agency / operator",
        title: "Production design at scale",
        body: "Agencies run high-volume production design across clients, each in its own brand system.",
      },
    ],
    limits: [
      {
        title: "Original art direction",
        body: "A brand-new visual identity from a blank page is your creative call. They explore options and extend the direction you pick.",
      },
      {
        title: "Print production",
        body: "Press checks, paper stock, and the physical result still need your eye.",
      },
      {
        title: "Taste is a partnership",
        body: "They learn your taste from your feedback. The first week calibrates; after that, revision rounds get rare.",
      },
    ],
    comparison3: {
      title: "Three ways to get design done",
      cols: ["Doing it yourself", "A design app", "AI Designer"],
      rows: [
        { label: "Turnaround", cells: ["Days per request", "As fast as you work", "Minutes to hours"] },
        { label: "Brand consistency", cells: ["Depends on the day", "Templates you maintain", "Same brand system on every asset"] },
        { label: "Formats", cells: ["Whatever you know", "Graphics and decks", "Graphics, decks, PDFs, icons, video"] },
        { label: "Works from a brief", cells: ["n/a", "From a prompt", "From your brief, in your brand system"] },
      ],
    },
    faq: [
      {
        q: "Can they handle ongoing production design?",
        a: "Yes: brand assets, social graphics, decks, one-pagers, and icons, delivered on brand and on time. Foundational identity work, like a brand-new logo, usually starts with your own creative direction; they then carry that direction into every asset.",
      },
      {
        q: "How do they learn our brand?",
        a: "Give them your brand kit, or even just your website, and they extract the system: colors, type, spacing, logo rules, tone. They keep that system in durable memory and apply it to every asset, and when your brand evolves, you tell them once and everything after follows.",
      },
      {
        q: "Is this just an image generator?",
        a: "No. Image generators produce a picture from a prompt. A CellCog AI Employee is a designer: they work from a brief, make layout and hierarchy decisions, produce multi-format deliverables like decks and PDFs, apply your brand system, and iterate on your feedback with memory of what you liked.",
      },
      {
        q: "What file formats do they deliver?",
        a: "PNG and JPG for graphics, SVG for icons and illustrations that need to scale, PDF for documents and decks. Your AI Designer delivers in the sizes and aspect ratios each channel needs, from a LinkedIn banner to a story format, without you resizing anything.",
      },
      {
        q: "Can they design a full brand identity from scratch?",
        a: "They can propose directions, build the chosen direction into a full system, and document the guidelines. For a flagship rebrand, most teams keep the creative direction themselves: your AI Designer does the exploration and the production, and the final call on taste stays yours.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Designer.",
      },
    ],
    ctaBand: {
      title: "Put your AI Designer to work today",
      body: "Send them your brand kit and one brief, and get back finished assets in your own visual language.",
    },
  },
  "ai-chief-of-staff": {
    slug: "ai-chief-of-staff",
    roleDisplay: "AI Chief of Staff",
    roleNoun: "chief of staff",
    article: "a",
    seo: {
      title: "AI Chief of Staff: Hire a CellCog AI Employee | CellCog",
      description: "AI Chief of Staff from CellCog AI Employee: run your operating cadence, prep decisions, track initiatives across teams, and keep the company moving. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee runs your operating cadence, preps every decision with the numbers and the options, tracks initiatives across the company, and makes sure the important things actually happen.",
    capabilities: [
      { title: "Run the operating cadence", body: "Own the weekly rhythm: agendas prepared, metrics pulled, notes captured, decisions logged, follow-ups chased." },
      { title: "Prep decisions", body: "Turn an open question into a decision memo: the data, the options, the tradeoffs, and a recommendation." },
      { title: "Track initiatives across teams", body: "Keep every strategic initiative visible: status, owners, blockers, and what needs your attention this week." },
      { title: "Turn strategy into execution", body: "Break the annual plan into quarterly goals and weekly commitments, and report honestly on how it is tracking." },
      { title: "Carry one consistent story", body: "Draft the board update, the investor email, and the all-hands narrative from one source of truth." },
    ],
    prompts: [
      {
        q: "Run my Monday leadership meeting: pull the metrics, build the agenda from open decisions, and send the pre-read the night before.",
        deliverable: "a recurring leadership meeting pack",
      },
      {
        q: "We're deciding whether to raise prices in Q4. Build the decision memo: data, customer impact, three options, and your recommendation.",
        deliverable: "a decision memo with a recommendation",
      },
      {
        q: "Track our five company initiatives. Every Friday, tell me which are on track, which are stuck, and what you did about the stuck ones.",
        deliverable: "a weekly initiative report with actions taken",
      },
      {
        q: "Draft the monthly investor update from our metrics and this month's wins and misses.",
        deliverable: "a ready-to-review investor update",
      },
      {
        q: "I have 40 open loops in my head. Interview me, get them all out, organize them, and tell me the five that matter this week.",
        deliverable: "an organized priority system",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "A right hand from day one",
        body: "Decisions prepped, follow-through chased, and nothing dropped.",
      },
      {
        badge: "Small team",
        title: "The operating cadence of a bigger company",
        body: "Small teams run real weekly rhythms: pre-reads, decision logs, and initiative tracking that actually stays current.",
      },
      {
        badge: "Agency / operator",
        title: "One chief of staff across every entity",
        body: "Operators running multiple businesses get one consistent operating system across all of them, with per-entity reporting.",
      },
    ],
    limits: [
      {
        title: "Executive presence",
        body: "They can sit in the meeting on Google Meet, Zoom or Microsoft Teams and answer what they are authorized to, but reading a board's mood and carrying the room stays yours. They make sure you walk in with the best possible preparation.",
      },
      {
        title: "People and politics",
        body: "Sensitive personnel matters and internal politics stay with you. Your AI Chief of Staff gives you the data and keeps the record.",
      },
      {
        title: "The final call",
        body: "A chief of staff advises; the executive decides. Your AI Chief of Staff frames every decision and never makes the strategic ones for you.",
      },
    ],
    comparison3: {
      title: "Three ways to run your operating cadence",
      cols: ["Doing it yourself", "A chatbot", "AI Chief of Staff"],
      rows: [
        { label: "Meeting prep", cells: ["When the calendar allows", "When you ask", "Pre-read the night before"] },
        { label: "Initiative tracking", cells: ["A weekly status chase", "No", "Live view, blockers flagged"] },
        { label: "Decision memos", cells: ["Days of preparation", "A draft from your prompt", "Hours, with sources cited"] },
        { label: "Chases follow-ups by email", cells: ["You", "No", "Yes, from their own inbox"] },
      ],
    },
    faq: [
      {
        q: "What does an AI chief of staff actually do?",
        a: "The job a chief of staff does: runs your operating rhythm, preps your decisions, tracks initiatives, chases follow-through, and drafts the communication that keeps everyone aligned. They work every day, keep durable memory of your goals and context, and report to you like a trusted right hand.",
      },
      {
        q: "How is this different from an AI executive assistant?",
        a: "An executive assistant manages your inbox, calendar, and logistics. A chief of staff manages your agenda in the larger sense: what the company is trying to do, whether it is happening, and what you should decide next. CellCog offers both roles, and many owners eventually hire one of each.",
      },
      {
        q: "Can they really prepare board-level material?",
        a: "Yes. They pull the real numbers from your systems, draft the narrative, and build the deck or memo, keeping a consistent story across board updates, investor emails, and internal communication. You review and own the final version, and they remember every edit you make for next time.",
      },
      {
        q: "How do they know what is happening across my company?",
        a: "You connect them to the systems where the work lives: project trackers, dashboards, documents, email. Your AI Chief of Staff reads continuously, maintains a single source of truth, and surfaces what needs your attention instead of waiting to be asked.",
      },
      {
        q: "Can they sit in on my leadership meeting?",
        a: "Yes. Invite them to the Google Meet, Zoom or Microsoft Teams and they join under their own name, camera off, with the pre-read they wrote already in hand. They answer the questions put to them, within what you have authorized, defer anything that would commit the company, and file the decisions and follow-ups on their task board after the call.",
      },
      {
        q: "Is my strategic information safe with them?",
        a: "Your data stays yours and is used only for your work. You control exactly which systems they can read, anything you mark sensitive requires your approval before it moves, and every action is logged so you can audit what they did.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Chief of Staff.",
      },
    ],
    ctaBand: {
      title: "Put your AI Chief of Staff to work today",
      body: "Give them your goals and your calendar, and get back an operating cadence that runs itself.",
    },
  },
  "ai-content-creator": {
    slug: "ai-content-creator",
    relatedPosts: ["best-ai-content-creators", "what-is-an-ai-employee", "how-to-hire-an-ai-employee", "ai-employee-cost"],
    roleDisplay: "AI Content Creator",
    roleNoun: "content creator",
    article: "a",
    seo: {
      title: "AI Content Creator: Hire a CellCog AI Employee | CellCog",
      description: "AI Content Creator from CellCog AI Employee: videos, social graphics, podcasts, and posts, produced end to end on a real content calendar. See prompts and how they compare.",
    },
    hook: "A CellCog AI Employee produces the actual media: videos, social graphics, podcast episodes, and the posts that carry them, runs the content calendar, and delivers on schedule.",
    capabilities: [
      { title: "Produce video content", body: "Short-form clips, product videos, and explainers: scripted, generated, edited, and captioned, ready to post." },
      { title: "Design social visuals", body: "Graphics, carousels, and thumbnails in your brand style, sized for every platform." },
      { title: "Make audio and podcasts", body: "Narration, podcast episodes, and audio versions of your written content, in consistent voices." },
      { title: "Run the content calendar", body: "Plan the month, keep the pipeline full, and adapt the plan to what actually performs." },
      { title: "Repurpose everything", body: "Turn one webinar into clips, posts, an article, and an email: every asset working in every format." },
    ],
    prompts: [
      {
        q: "Take this customer case study and turn it into a 60-second video with captions, three quote graphics, and a LinkedIn post.",
        deliverable: "a full content package from one source",
      },
      {
        q: "Plan next month's content calendar around our product launch: two posts a week, one video a week, themes and hooks included.",
        deliverable: "a complete content calendar",
      },
      {
        q: "Turn our latest blog post into a 5-minute podcast episode with an intro and outro.",
        deliverable: "a produced podcast episode",
      },
      {
        q: "Make 6 thumbnail options for this video, each testing a different hook.",
        deliverable: "thumbnail options ready to test",
      },
      {
        q: "Our competitor just launched. Draft a same-day response post with a comparison graphic.",
        deliverable: "a timely post with a designed graphic",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "A content engine of one",
        body: "Founders publish like a team: video, graphics, and posts on a real calendar, from one role they brief once.",
      },
      {
        badge: "Small team",
        title: "Consistent output without a studio",
        body: "Small teams keep a full multi-format pipeline running: the calendar stays full even when everyone is busy shipping.",
      },
      {
        badge: "Agency / operator",
        title: "Content production across every client",
        body: "Agencies produce video, graphics, and posts for many clients in parallel, each in its own brand and voice.",
      },
    ],
    limits: [
      {
        title: "On-camera presence",
        body: "They cannot film you at your desk. They can build videos around your footage, your avatar, or no presenter at all.",
      },
      {
        title: "Lived experience",
        body: "Authentic first-person stories come from you. Your AI Content Creator turns your raw takes into finished, publishable content.",
      },
      {
        title: "Platform rules",
        body: "Some platforms restrict AI-generated content. Your AI Content Creator flags where that applies so your distribution stays clean.",
      },
    ],
    faq: [
      {
        q: "What content can they actually produce?",
        a: "Finished, publishable assets: short-form and long-form video with captions, social graphics and carousels, podcast episodes and narration, thumbnails, and the posts themselves. Your AI Content Creator is one role working across every format, so a single brief becomes a coordinated set of assets.",
      },
      {
        q: "How is this different from an AI content writer?",
        a: "A content writer works in text: articles, SEO content, email. A content creator works in every format: video, images, audio, and the posts that carry them. CellCog offers both roles. If your bottleneck is producing media rather than words, the content creator is the one you want.",
      },
      {
        q: "Is the quality actually good enough to publish?",
        a: "You review everything before it goes out, and they learn your bar from your feedback: what you approve, what you change, what you reject. Quality compounds because what they learn in one shift carries into the next, so the tenth video needs far fewer notes than the first.",
      },
      {
        q: "Can they publish directly to our channels?",
        a: "They can draft, schedule, and publish through connected tools where you allow it, or hand you ready-to-post assets for final say. Most owners start with approval on every post and loosen it as trust builds. Where a platform restricts automated or AI content, your AI Content Creator tells you.",
      },
      {
        q: "Do they keep a consistent brand voice and style?",
        a: "Yes. They learn your visual system and your voice once and keep them in durable memory, so every asset matches the last. When you evolve the brand, you tell them once and every future asset follows.",
      },
      {
        q: "How does the cost compare to hiring a content creator?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Content Creator.",
      },
    ],
    ctaBand: {
      title: "Put your AI Content Creator to work today",
      body: "Send them one piece of source material and get back the video, the graphics, and the posts to carry it.",
    },
  },
  "ai-head-of-growth": {
    slug: "ai-head-of-growth",
    roleDisplay: "AI Head of Growth",
    roleNoun: "head of growth",
    article: "a",
    seo: {
      title: "AI Head of Growth: Hire a CellCog AI Employee | CellCog",
      description: "AI Head of Growth from CellCog AI Employee: owns your growth engine, SEO and content, launches and the dashboards that prove it. See how they compare.",
    },
    hook: "A CellCog AI Employee owns the growth engine end to end: the content and SEO that bring people in, the launches, the funnel numbers, and the dashboards that show what moved.",
    capabilities: [
      { title: "Own the growth engine", body: "Set the north-star metric, pick the channels, run the experiments, and report what moved every week." },
      { title: "Write and ship the content", body: "Blog posts, comparison pages, launch records and living trackers: researched from primary sources, charted, and published on a schedule." },
      { title: "Run SEO and answer-engine visibility", body: "Keyword and citation research, page updates, indexing, and honest reads of what Google and AI assistants actually cite." },
      { title: "Measure the funnel", body: "Signups, activation, retention and revenue from your real data, in dashboards that refresh themselves." },
      { title: "Produce in every format", body: "Research reports, code, dashboards, PDFs, video, images and spreadsheets: whatever the growth work needs that week." },
    ],
    prompts: [
      {
        q: "Build our growth dashboard from Stripe and the product database: signups, activation, paying customers, weekly, with definitions I can trust.",
        deliverable: "a live KPI dashboard with written definitions",
      },
      {
        q: "A competitor just raised a round and is loud about it. Write the comparison page and the blog post, sourced from their own docs.",
        deliverable: "a comparison page and a post, the same day",
      },
      {
        q: "Audit what AI assistants say when someone asks for the best tool in our category, and tell me where we are missing.",
        deliverable: "an answer-engine visibility read with the gaps named",
      },
      {
        q: "Plan and run our Product Hunt launch: the page, the daily posts, the follow-up, and the read afterwards.",
        deliverable: "a launch run end to end",
      },
      {
        q: "Set up a living tracker for the model release everyone is waiting for, and update it the hour it ships.",
        deliverable: "a page that captures the search wave",
      },
    ],
    useCases: [
      {
        badge: "Solo founder",
        title: "A growth lead from day one",
        body: "Founders get channel strategy, content and honest numbers from day one, from one role that remembers every decision.",
      },
      {
        badge: "Small team",
        title: "Growth that compounds while you build",
        body: "Small teams keep SEO, content and funnel reads moving every week without pulling an engineer or the founder off the product.",
      },
      {
        badge: "Agency / operator",
        title: "One growth engine across every brand",
        body: "Operators run content, SEO and reporting for several businesses in parallel, each in its own voice and with its own numbers.",
      },
    ],
    limits: [
      {
        title: "Founder voice",
        body: "Your own posts and your investor story stay yours. Your AI Head of Growth brings the data and the drafts you ask for, and they never speak as you.",
      },
      {
        title: "Paid spend and public posting",
        body: "They do not spend money or publish under your brand without your say. You set the approvals, and they work inside them.",
      },
      {
        title: "Strategy calls",
        body: "They recommend, with evidence. Which market to enter and what to charge are decisions you make, with better information in front of you.",
      },
    ],
    comparison3: {
      title: "Three ways to run growth",
      cols: ["Doing it yourself", "An analytics tool", "AI Head of Growth"],
      rows: [
        { label: "Content output", cells: ["A post a week, on a good week", "No", "Researched posts on a daily cadence"] },
        { label: "Funnel reads", cells: ["A monthly deck", "Dashboards you read", "Reads them and tells you what moved"] },
        { label: "Reaction to market news", cells: ["Days", "No", "The same hour, on a living page"] },
        { label: "Asks before publishing", cells: ["n/a", "n/a", "Yes, by default"] },
      ],
    },
    faq: [
      {
        q: "What does an AI head of growth actually do?",
        a: "The job a head of growth does: picks the channels, runs the experiments, writes and ships the content, watches the funnel and reports honestly on what moved. They keep durable memory of your positioning, your numbers and every decision, so the plan compounds instead of restarting every month.",
      },
      {
        q: "Is this really how CellCog runs its own growth?",
        a: "Yes. CellCog's Head of Growth is an AI employee. Every post on the CellCog blog is written by them and reviewed by the founder: the research, the source checks, the charts and the publishing. The comparison pages, the release trackers and the growth dashboards the founder reads every morning are the same employee's work. So is this page.",
      },
      {
        q: "How is this different from an AI marketing manager?",
        a: "A marketing manager owns campaigns, brand and the content calendar. A head of growth owns the number: which channels bring paying customers, what converts them, what keeps them, and the experiments that move each step. CellCog offers both roles, and the two work well as a pair on one team.",
      },
      {
        q: "Can they work with our analytics and data?",
        a: "Yes. Connect Google Analytics, Search Console, Stripe, your product database or wherever your numbers live, read access first. Your AI Head of Growth pulls the data, writes down the definition of every metric, and builds dashboards that refresh on a schedule. Anything that writes to a live system waits for your approval.",
      },
      {
        q: "Will they publish content without asking me?",
        a: "Only if you allow it. Most owners start with review on every post and open the gate as trust builds. Publishing, spending and outreach are approvals you grant per class of action, and your AI Head of Growth works inside them. What you approve once, they remember, so you are never asked the same question twice.",
      },
      {
        q: "What do they cost?",
        a: COST_FAQ_ANSWER,
      },
      {
        q: "How do I get started?",
        a: "Click \"Get started\" to sign up and hire one in minutes, or use \"Talk to us\" and our team will help you set up your AI Head of Growth.",
      },
    ],
    ctaBand: {
      title: "Put your AI Head of Growth to work today",
      body: "Give them your goals and read access to your numbers, and get back a growth engine that reports every week.",
    },
  },
};

/** Ordered slugs for the related-roles grid and the hub roles section. */
export const ROLE_SLUGS = Object.keys(ROLE_CONFIGS);
